import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaRupeeSign, FaTrash, FaPlus, FaCheck, FaTimes, FaUserGraduate, FaChartBar, FaCalendarAlt, FaEdit, FaToggleOn, FaToggleOff, FaInfoCircle, FaTags, FaPercent, FaCreditCard, FaGraduationCap, FaBars, FaMoneyBillWave, FaUserFriends, FaSpinner } from 'react-icons/fa';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import ErrorBoundary from '../../Components/ErrorBoundary';
import UserContext from '../../Components/UserContext';
import HttpInterceptor from '../../lib/HttpInterceptor';
import ConfirmDialog from '../../Components/ConfirmDialog';
import { DEBOUNCE_DELAY, TIME_CONSTANTS } from '../../constants/appConstants';
import { useDateTimeSettings } from '../../hooks/useDateTimeSettings';
import { formatDate as formatDateUtil } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/currencyFormatter';

/**
 * Tooltip Component with fixed positioning to avoid clipping
 */
const InfoTooltip = ({ children, content, width = 'w-56' }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      
      // Get actual tooltip width if available, otherwise use approximate
      let tooltipWidth = width === 'w-72' ? 288 : width === 'w-64' ? 256 : 224;
      if (tooltipRef.current) {
        tooltipWidth = tooltipRef.current.offsetWidth || tooltipWidth;
      }
      
      // For fixed positioning, use viewport coordinates directly (getBoundingClientRect gives viewport coords)
      // Position tooltip below the icon, centered horizontally
      const left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      const top = rect.bottom + 8;
      
      // Ensure tooltip doesn't go off-screen horizontally
      const maxLeft = window.innerWidth - tooltipWidth - 16;
      const minLeft = 16;
      const finalLeft = Math.max(minLeft, Math.min(left, maxLeft));
      
      // Ensure tooltip doesn't go below viewport
      const viewportHeight = window.innerHeight;
      const tooltipHeight = tooltipRef.current ? tooltipRef.current.offsetHeight : 150;
      const maxTop = viewportHeight - tooltipHeight - 16;
      const finalTop = Math.max(16, Math.min(top, maxTop));
      
      setPosition({ top: finalTop, left: finalLeft });
    }
  }, [width]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    // Calculate position immediately
    setTimeout(() => {
      updatePosition();
    }, 10);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  useEffect(() => {
    if (isHovered && iconRef.current) {
      // Calculate position when tooltip becomes visible
      const calculatePosition = () => {
        if (iconRef.current) {
          updatePosition();
        }
      };
      
      // Use multiple attempts to ensure position is calculated
      calculatePosition();
      const timeout1 = setTimeout(calculatePosition, 0);
      const timeout2 = setTimeout(calculatePosition, 50);
      
      const handleResize = () => {
        calculatePosition();
      };
      const handleScroll = () => {
        calculatePosition();
      };
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isHovered, updatePosition]);

  return (
    <>
      <span 
        ref={iconRef}
        className="inline-block cursor-help"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>
      {isHovered && typeof document !== 'undefined' && createPortal(
        <div
          ref={tooltipRef}
          className={`${width} max-w-[90vw] p-2 sm:p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl border border-gray-700`}
          style={{
            position: 'fixed',
            top: position.top > 0 ? `${position.top}px` : '50%',
            left: position.left > 0 ? `${position.left}px` : '50%',
            transform: position.top > 0 && position.left > 0 ? 'none' : 'translate(-50%, -50%)',
            zIndex: 9999999,
            pointerEvents: 'auto',
            isolation: 'isolate'
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {typeof content === 'string' ? <p className="text-gray-300">{content}</p> : content}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 pointer-events-none">
            <div className="border-4 border-transparent border-b-gray-900"></div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

/**
 * Debounce utility function
 * Delays function execution until after wait time has passed since last invocation
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function with cancel method
 */
const debounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return debounced;
};

function FeePlan() {
  console.log('=== FEE PLAN: Component rendered ===');
  console.log('=== FEE PLAN: Component mount time:', new Date().toISOString());
  const { session } = useContext(UserContext);
  console.log('=== FEE PLAN: Session from context:', !!session, session?.trainerId || session?.id);
  const navigate = useNavigate();
  
  // Check if user is a consultant
  const trainerServices = (session?.services && session.services.length > 0) ? session.services : (session?.service ? [session.service] : []);
  const servicePartnerTypes = session?.servicePartnerTypes || {};
  const activeService = localStorage.getItem('activeService') || trainerServices[0] || '';
  const activePartnerType = activeService ? (servicePartnerTypes[activeService] || 'instructor') : 'instructor';
  const isConsultant = activePartnerType === 'consultant';
  const [programs, setPrograms] = useState([]);
  console.log('=== FEE PLAN: Initial programs state:', programs.length);
  const [activeTab, setActiveTab] = useState(0); // 0 = Program Pricing, 1 = Promo Codes, etc.
  const [isPWA, setIsPWA] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [installmentPlans, setInstallmentPlans] = useState([]);
  const [scholarshipOffers, setScholarshipOffers] = useState([]);
  const [scholarshipApplications, setScholarshipApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isInitialFetch = useRef(true);
  const [showLoading, setShowLoading] = useState(false);
  const [stackingEnabled, setStackingEnabled] = useState(false);

  // New state for program selection and date management
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedProgramDetails, setSelectedProgramDetails] = useState(null);
  const [isLoadingProgramDetails, setIsLoadingProgramDetails] = useState(false);

  // Edit mode states
  const [editingPromo, setEditingPromo] = useState(null);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [editingScholarship, setEditingScholarship] = useState(null);

  // Confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  const [showForceDeleteConfirm, setShowForceDeleteConfirm] = useState(false);
  const [showDeleteScholarshipConfirm, setShowDeleteScholarshipConfirm] = useState(null);
  
  // Program status toggle state
  const [togglingProgramStatus, setTogglingProgramStatus] = useState(false);

  // Referral settings state
  const [referralSettings, setReferralSettings] = useState({
    referralDiscountType: 'percentage',
    referralDiscountPercentage: 0,
    referralDiscountAmount: 0,
    referralDiscountForReferredStudent: false,
    referralSystemEnabled: true,
    referralCodeExpiryDays: 90
  });
  const [referralAnalytics, setReferralAnalytics] = useState(null);
  const [isLoadingReferralSettings, setIsLoadingReferralSettings] = useState(false);
  const [hasReferralFeature, setHasReferralFeature] = useState(false);
  const [showReferralSystemConfirm, setShowReferralSystemConfirm] = useState(false);
  const [pendingReferralSystemEnabled, setPendingReferralSystemEnabled] = useState(null);

  // Form states
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount: '',
    validFrom: '',
    validUntil: '',
    programIds: [],
    applyToAll: false,
    hasPriority: false,
  });

  const [newDiscount, setNewDiscount] = useState({
    name: '',
    discount: '',
    validFrom: '',
    validUntil: '',
    programIds: [],
    applyToAll: false,
    hasPriority: false,
  });

  const [newInstallment, setNewInstallment] = useState({
    name: '',
    programId: '',
    numberOfInstallments: 2,
    intervals: [
      { dueDate: new Date(Date.now() + TIME_CONSTANTS.MONTH).toISOString(), percentage: 50 },
      { dueDate: new Date(Date.now() + TIME_CONSTANTS.MONTH * 2).toISOString(), percentage: 50 },
    ],
    isActive: true,
  });

  const [newScholarshipOffer, setNewScholarshipOffer] = useState({
    name: '',
    programId: '',
    discountPercentage: '',
    description: '',
    isActive: true,
  });

  const [pricing, setPricing] = useState({
    programId: '',
    price: '',
  });

  const programNameCache = useMemo(() => {
    return new Map(programs.map(p => [p._id.toString(), p.name]));
  }, [programs]);

  const memoizedPrograms = useMemo(() => programs, [programs]);

  // Dashboard statistics
  const dashboardStats = useMemo(() => {
    const now = new Date();
    const activePromos = promoCodes.filter(promo => 
      new Date(promo.validFrom) <= now && new Date(promo.validUntil) >= now
    );
    const activeDiscounts = discounts.filter(discount => 
      new Date(discount.validFrom) <= now && new Date(discount.validUntil) >= now
    );
    const pendingApplications = scholarshipApplications.filter(app => app.status === 'pending');
    const programsLength = programs.length;
    
    return {
      totalPrograms: programsLength,
      activePrograms: programs.filter(p => p.isActive).length,
      totalPromoCodes: promoCodes.length,
      activePromos: activePromos.length,
      totalDiscounts: discounts.length,
      activeDiscounts: activeDiscounts.length,
      totalInstallmentPlans: installmentPlans.length,
      totalScholarshipOffers: scholarshipOffers.length,
      pendingApplications: pendingApplications.length,
    };
  }, [programs, promoCodes, discounts, installmentPlans, scholarshipOffers, scholarshipApplications]);

  // New function to fetch program dates
  const fetchProgramDetails = async (programId) => {
    if (!programId) {
      setSelectedProgramDetails(null);
      return;
    }

    try {
      setIsLoadingProgramDetails(true);
      const response = await HttpInterceptor.get(`/api/programs/${programId}`);
      const programData = response.data;
      setSelectedProgramDetails(programData);
      
      // If installment plan form has this program selected, recalculate intervals based on program's effectiveDate
      if (newInstallment.programId === programId && programData?.effectiveDate) {
        const baseDate = new Date(programData.effectiveDate);
        const updatedIntervals = newInstallment.intervals.length > 0 
          ? newInstallment.intervals.map((interval, index) => {
              // Calculate days from the interval's current dueDate relative to baseDate
              const currentDueDate = new Date(interval.dueDate);
              const daysDiff = Math.round((currentDueDate.getTime() - baseDate.getTime()) / (24 * 60 * 60 * 1000));
              // Recalculate based on new baseDate
              return {
                ...interval,
                dueDate: new Date(baseDate.getTime() + daysDiff * 24 * 60 * 60 * 1000).toISOString()
              };
            })
          : [
              { dueDate: new Date(baseDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(), percentage: 50 },
              { dueDate: new Date(baseDate.getTime() + 60 * 24 * 60 * 60 * 1000).toISOString(), percentage: 50 },
            ];
        
        setNewInstallment(prev => ({
          ...prev,
          intervals: updatedIntervals,
          numberOfInstallments: updatedIntervals.length
        }));
      }
    } catch (error) {
      console.error('Failed to fetch program details:', error);
      toast.error('Failed to fetch program details');
      setSelectedProgramDetails(null);
    } finally {
      setIsLoadingProgramDetails(false);
    }
  };

  // Handle program selection
  const handleProgramSelection = (programId) => {
    setSelectedProgram(programId);
    fetchProgramDetails(programId);
    
    // Reset date fields when program changes
    setNewPromo(prev => ({
      ...prev,
      validFrom: '',
      validUntil: ''
    }));
    setNewDiscount(prev => ({
      ...prev,
      validFrom: '',
      validUntil: ''
    }));
    
    // Reset installment plan form - dates will be recalculated when program details are loaded
    setNewInstallment({
      name: '',
      programId: programId,
      numberOfInstallments: 2,
      intervals: [],
      isActive: true,
    });
  };

  // Get minimum date for date inputs (always use current date so students enrolling before program starts can use promo codes)
  const getMinDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().split('T')[0];
  };

  // Move fetch functions before fetchAllData and wrap in useCallback for stability
  const fetchProgramsWithPricing = useCallback(async () => {
    try {
      console.log('=== FEE PLAN: Fetching programs with pricing ===');
      console.log('Session:', session);
      console.log('TrainerId from session:', session?.trainerId || session?.id);
      console.log('API endpoint: /api/fee-plan/pricing');
      
      const response = await HttpInterceptor.get('/api/fee-plan/pricing');
      
      console.log('=== FEE PLAN: API Response Received ===');
      console.log('Raw API response:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Is array?', Array.isArray(response.data));
      console.log('Response data length:', Array.isArray(response.data) ? response.data.length : 'N/A');

      // Handle different response formats
      let programsData = [];
      if (Array.isArray(response.data)) {
        programsData = response.data;
        console.log('✅ Using response.data as array, length:', programsData.length);
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        programsData = response.data.data;
        console.log('✅ Using response.data.data as array, length:', programsData.length);
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        programsData = response.data.data;
        console.log('✅ Using response.data.data (success), length:', programsData.length);
      } else {
        console.warn('❌ Unexpected response format:', response.data);
        console.warn('Response.data keys:', Object.keys(response.data || {}));
      }

      console.log('=== FEE PLAN: ProgramsData after parsing ===');
      console.log('ProgramsData:', programsData);
      console.log('ProgramsData length:', programsData.length);
      console.log('Is array?', Array.isArray(programsData));

      if (!programsData || programsData.length === 0) {
        console.warn('❌ No programs data received from API. Response:', response);
        return [];
      }

      const programsWithPricing = programsData.map(program => {
        if (!program) return null;
        
        return {
          ...program,
          _id: program._id || program.id || '',
          name: program.name || 'Unnamed Program',
          price: program.price !== undefined && program.price !== null 
            ? Number(program.price) 
            : (program.currentPrice !== undefined && program.currentPrice !== null 
            ? Number(program.currentPrice) 
              : (program.basePrice !== undefined && program.basePrice !== null 
                ? Number(program.basePrice) 
                : null)),
          isActive: program.isActive ?? true,
        };
      }).filter(Boolean); // Remove null entries

      if (programsWithPricing.length > 0 && programsWithPricing.every(p => p.price === null)) {
        toast.info('No pricing set for any programs. Please set pricing in the Program Pricing tab.');
      }

      return programsWithPricing;
    } catch (error) {
      console.error('Failed to load programs:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load programs';
      toast.error(errorMsg);
      throw error;
    }
  }, [session]);

  const fetchPromoCodes = useCallback(async () => {
    try {
      console.log('[FeePlan] Fetching promo codes...');
      const response = await HttpInterceptor.get('/api/fee-plan/promo-codes');
      console.log('[FeePlan] Promo codes response:', response);
      console.log('[FeePlan] Response data:', response.data);
      console.log('[FeePlan] Is array?', Array.isArray(response.data));
      
      let promoData = [];
      if (Array.isArray(response.data)) {
        promoData = response.data;
        console.log('[FeePlan] Using response.data as array, length:', promoData.length);
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        promoData = response.data.data;
        console.log('[FeePlan] Using response.data.data (success), length:', promoData.length);
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        promoData = response.data.data;
        console.log('[FeePlan] Using response.data.data, length:', promoData.length);
      } else {
        console.warn('[FeePlan] Unexpected promo codes response format:', response.data);
      }

      if (!promoData || promoData.length === 0) {
        console.log('[FeePlan] No promo codes found');
        return [];
      }

      const processedPromos = promoData
        .filter(promo => promo != null)
        .map(promo => {
          const programIds = Array.isArray(promo.programIds) 
            ? promo.programIds.map(id => id?.toString() || '').filter(Boolean)
            : [];
          return {
            ...promo,
            id: promo._id || promo.id || '',
            code: promo.code || '',
            discount: Number(promo.discount || promo.discountPercentage || 0),
            programIds,
          };
        });
      
      console.log('[FeePlan] Processed promo codes:', processedPromos.length, processedPromos);
      return processedPromos;
    } catch (error) {
      console.error('[FeePlan] Failed to load promo codes:', error);
      console.error('[FeePlan] Error details:', error.response?.data || error.message);
      // Don't throw, return empty array instead to allow other data to load
      return [];
    }
  }, []);

  const fetchDiscounts = useCallback(async () => {
    try {
      console.log('[FeePlan] Fetching discounts...');
      const response = await HttpInterceptor.get('/api/fee-plan/discounts');
      console.log('[FeePlan] Discounts response:', response);
      console.log('[FeePlan] Response data:', response.data);
      console.log('[FeePlan] Is array?', Array.isArray(response.data));
      
      let discountData = [];
      if (Array.isArray(response.data)) {
        discountData = response.data;
        console.log('[FeePlan] Using response.data as array, length:', discountData.length);
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        discountData = response.data.data;
        console.log('[FeePlan] Using response.data.data (success), length:', discountData.length);
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        discountData = response.data.data;
        console.log('[FeePlan] Using response.data.data, length:', discountData.length);
      } else {
        console.warn('[FeePlan] Unexpected discounts response format:', response.data);
      }

      if (!discountData || discountData.length === 0) {
        console.log('[FeePlan] No discounts found');
        return [];
      }

      const processedDiscounts = discountData
        .filter(discount => discount != null)
        .map(discount => {
          const programIds = Array.isArray(discount.programIds) 
            ? discount.programIds.map(id => id?.toString() || '').filter(Boolean)
            : [];
          return {
            ...discount,
            id: discount._id || discount.id || '',
            name: discount.name || '',
            discount: Number(discount.discount || discount.discountPercentage || 0),
            programIds,
          };
        });
      
      console.log('[FeePlan] Processed discounts:', processedDiscounts.length, processedDiscounts);
      return processedDiscounts;
    } catch (error) {
      console.error('[FeePlan] Failed to load discounts:', error);
      console.error('[FeePlan] Error details:', error.response?.data || error.message);
      // Don't throw, return empty array instead to allow other data to load
      return [];
    }
  }, []);

  const fetchInstallmentPlans = useCallback(async () => {
    try {
      console.log('[FeePlan] Fetching installment plans...');
      const response = await HttpInterceptor.get('/api/fee-plan/installments');
      console.log('[FeePlan] Installment plans response:', response);
      console.log('[FeePlan] Response data:', response.data);
      console.log('[FeePlan] Is array?', Array.isArray(response.data));
      
      let installmentData = [];
      if (Array.isArray(response.data)) {
        installmentData = response.data;
        console.log('[FeePlan] Using response.data as array, length:', installmentData.length);
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        installmentData = response.data.data;
        console.log('[FeePlan] Using response.data.data, length:', installmentData.length);
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        installmentData = response.data.data;
        console.log('[FeePlan] Using response.data.data (success), length:', installmentData.length);
      } else {
        console.warn('[FeePlan] Unexpected installment plans response format:', response.data);
      }
      
      const processed = Array.isArray(installmentData) ? installmentData.filter(Boolean) : [];
      console.log('[FeePlan] Processed installment plans:', processed.length, processed);
      return processed;
    } catch (error) {
      console.error('[FeePlan] Failed to load installment plans:', error);
      console.error('[FeePlan] Error details:', error.response?.data || error.message);
      // Don't throw, return empty array instead to allow other data to load
      return [];
    }
  }, []);

  const fetchScholarshipOffers = useCallback(async () => {
    try {
      console.log('[FeePlan] Fetching scholarship offers...');
      const response = await HttpInterceptor.get('/api/fee-plan/scholarship-offers');
      console.log('[FeePlan] Scholarship offers response:', response);
      console.log('[FeePlan] Response data:', response.data);
      console.log('[FeePlan] Is array?', Array.isArray(response.data));
      
      let scholarshipData = [];
      if (Array.isArray(response.data)) {
        scholarshipData = response.data;
        console.log('[FeePlan] Using response.data as array, length:', scholarshipData.length);
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        scholarshipData = response.data.data;
        console.log('[FeePlan] Using response.data.data, length:', scholarshipData.length);
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        scholarshipData = response.data.data;
        console.log('[FeePlan] Using response.data.data (success), length:', scholarshipData.length);
      } else {
        console.warn('[FeePlan] Unexpected scholarship offers response format:', response.data);
      }
      
      const processed = Array.isArray(scholarshipData) ? scholarshipData.filter(Boolean) : [];
      console.log('[FeePlan] Processed scholarship offers:', processed.length, processed);
      return processed;
    } catch (error) {
      console.error('[FeePlan] Failed to load scholarship offers:', error);
      console.error('[FeePlan] Error details:', error.response?.data || error.message);
      // Don't throw, return empty array instead to allow other data to load
      return [];
    }
  }, []);

  const fetchScholarshipApplications = useCallback(async () => {
    try {
      const response = await HttpInterceptor.get('/api/fee-plan/scholarship-applications');
      let applicationData = [];
      if (Array.isArray(response.data)) {
        applicationData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        applicationData = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        applicationData = response.data.data;
      }
      return Array.isArray(applicationData) ? applicationData.filter(Boolean) : [];
    } catch (error) {
      console.error('Failed to load scholarship applications:', error);
      // Don't throw, return empty array instead to allow other data to load
      return [];
    }
  }, []);

  const fetchStackingPreference = useCallback(async () => {
    try {
      const response = await HttpInterceptor.get('/api/fee-plan/stacking-preference');
      return response.data?.data?.enabled || response.data?.enabled || false;
    } catch (error) {
      console.error('Failed to load stacking preference:', error);
      throw error;
    }
  }, []);

  const fetchAllDataRef = useRef(null);
  const isFetchingRef = useRef(false);

  const fetchAllData = useCallback(async () => {
    console.log('=== FEE PLAN: fetchAllData called ===');
    if (!session) {
      console.log('=== FEE PLAN: No session, returning ===');
      return;
    }
    
    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      console.log('=== FEE PLAN: Already fetching, skipping ===');
      return;
    }
    
    isFetchingRef.current = true;
    
    // Clear any existing timeout
    if (fetchAllDataRef.current?.loadingTimeout) {
      clearTimeout(fetchAllDataRef.current.loadingTimeout);
    }
    
    // Track if this fetch is cancelled
    const fetchId = Date.now();
    fetchAllDataRef.current = { fetchId, loadingTimeout: null, cancelled: false };
    
    try {
      console.log('=== FEE PLAN: Setting isLoading to true ===');
      setIsLoading(true);
      setShowLoading(false);
      fetchAllDataRef.current.loadingTimeout = setTimeout(() => {
        if (fetchAllDataRef.current?.fetchId === fetchId && !fetchAllDataRef.current.cancelled) {
          setShowLoading(true);
        }
      }, 300);

      console.log('=== FEE PLAN: Starting fetchAllData ===');
      let programsData = [];
      try {
        console.log('=== FEE PLAN: Calling fetchProgramsWithPricing ===');
        programsData = await fetchProgramsWithPricing();
        console.log('=== FEE PLAN: Programs fetched successfully ===');
        console.log('Programs count:', programsData.length);
        console.log('Programs data:', programsData);
      } catch (error) {
        console.error('=== FEE PLAN: Error fetching programs ===');
        console.error('Error details:', error);
        console.error('Error message:', error.message);
        console.error('Error response:', error.response);
        // Don't throw, just use empty array to allow other data to load
        programsData = [];
      }
      
      // Check if fetch was cancelled or outdated
      if (fetchAllDataRef.current?.fetchId !== fetchId || fetchAllDataRef.current.cancelled) {
        console.log('=== FEE PLAN: Fetch cancelled or outdated ===');
        console.log('Current fetchId:', fetchId);
        console.log('Ref fetchId:', fetchAllDataRef.current?.fetchId);
        console.log('Cancelled:', fetchAllDataRef.current?.cancelled);
        // Still set programs even if cancelled, as long as we got data
        if (programsData.length > 0) {
          console.log('=== FEE PLAN: Setting programs despite cancellation (we have data) ===');
          setPrograms(programsData);
          console.log('=== FEE PLAN: Programs state set (despite cancellation) ===');
        }
        // Don't return here - continue to set other data
      } else {
        console.log('=== FEE PLAN: Setting programs state ===');
        console.log('Setting programs to:', programsData.length, 'programs');
        setPrograms(programsData);
        console.log('=== FEE PLAN: Programs state set ===');
      }

      const [
        promoCodesData,
        discountsData,
        installmentData,
        scholarshipOffersData,
        scholarshipApplicationsData,
        stackingData,
      ] = await Promise.all([
        fetchPromoCodes().catch(err => {
          console.error('Promo codes fetch error:', err);
          return [];
        }),
        fetchDiscounts().catch(err => {
          console.error('Discounts fetch error:', err);
          return [];
        }),
        fetchInstallmentPlans().catch(err => {
          console.error('Installment plans fetch error:', err);
          return [];
        }),
        fetchScholarshipOffers().catch(err => {
          console.error('Scholarship offers fetch error:', err);
          return [];
        }),
        fetchScholarshipApplications().catch(err => {
          console.error('Scholarship applications fetch error:', err);
          return [];
        }),
        fetchStackingPreference().catch(err => {
          console.error('Stacking preference fetch error:', err);
          return false;
        }),
      ]);

      // Check if fetch was cancelled, but still set data if we have valid results
      const wasCancelled = fetchAllDataRef.current?.fetchId !== fetchId || fetchAllDataRef.current.cancelled;
      
      if (wasCancelled) {
        console.log('[FeePlan] Fetch cancelled after Promise.all, but setting data anyway');
        console.log('[FeePlan] Data received:', {
          promoCodes: promoCodesData.length,
          discounts: discountsData.length,
          installmentPlans: installmentData.length,
          scholarshipOffers: scholarshipOffersData.length,
        });
      }

      if (fetchAllDataRef.current?.loadingTimeout) {
        clearTimeout(fetchAllDataRef.current.loadingTimeout);
      }

      console.log('[FeePlan] Setting state with data:', {
        promoCodes: promoCodesData.length,
        discounts: discountsData.length,
        installmentPlans: installmentData.length,
        scholarshipOffers: scholarshipOffersData.length,
        scholarshipApplications: scholarshipApplicationsData.length
      });
      
      // Always set the state, even if fetch was cancelled (similar to programs)
      setPromoCodes(promoCodesData);
      setDiscounts(discountsData);
      setInstallmentPlans(installmentData);
      setScholarshipOffers(scholarshipOffersData);
      setScholarshipApplications(scholarshipApplicationsData);
      setStackingEnabled(stackingData);
      setError(null);
      
      console.log('[FeePlan] State set successfully');
    } catch (err) {
      if (fetchAllDataRef.current?.fetchId !== fetchId || fetchAllDataRef.current.cancelled) return;
      
      console.error('API Error:', err);
      
      // If programs weren't set yet, set to empty array to prevent "No programs" message from showing incorrectly
      if (programs.length === 0) {
        setPrograms([]);
      }
      
      // Handle different error types
      let errorMessage = 'Failed to load data';
      if (err.type === 'network') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (err.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (err.status === 403) {
        errorMessage = 'You don\'t have permission to view this data.';
      } else if (err.status >= 500) {
        errorMessage = 'We’re having trouble right now. Please try again in a moment.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError({ message: errorMessage, type: err.type, status: err.status });
      // Only show toast if it's a server error (HttpInterceptor handles network errors)
      if (err.type !== 'network' && err.status !== 401) {
        toast.error(errorMessage);
      }
      
      // Set empty arrays on error to show empty state
      setPrograms([]);
      setPromoCodes([]);
      setDiscounts([]);
      setInstallmentPlans([]);
      setScholarshipOffers([]);
      setScholarshipApplications([]);
    } finally {
      // Always clear loading state, even if fetch was cancelled
      if (fetchAllDataRef.current?.loadingTimeout) {
        clearTimeout(fetchAllDataRef.current.loadingTimeout);
      }
      console.log('=== FEE PLAN: Setting isLoading to false in finally ===');
      setIsLoading(false);
      setShowLoading(false);
      isFetchingRef.current = false;
    }
  }, [session, fetchProgramsWithPricing, fetchPromoCodes, fetchDiscounts, fetchInstallmentPlans, fetchScholarshipOffers, fetchScholarshipApplications, fetchStackingPreference]);

  // Debounced version of fetchAllData
  const debouncedFetchAllDataRef = useRef(null);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Store latest fetchAllData in a ref to avoid dependency issues
  const fetchAllDataRefLatest = useRef(fetchAllData);
  useEffect(() => {
    fetchAllDataRefLatest.current = fetchAllData;
  }, [fetchAllData]);

  useEffect(() => {
    // Only run if we have a session
    if (!session) {
      return;
    }
    
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      console.log('=== FEE PLAN: Already fetching, skipping useEffect ===');
      return;
    }
    
    // Only fetch on initial mount or when session changes
    if (isInitialFetch.current) {
      console.log('=== FEE PLAN: Initial fetch triggered ===');
      console.log('Session available:', !!session);
      console.log('TrainerId:', session?.trainerId || session?.id);
      isInitialFetch.current = false;
      // Call fetchAllData using ref to get latest version
      console.log('=== FEE PLAN: Calling fetchAllData directly ===');
      fetchAllDataRefLatest.current();
    }
    
    // Only cleanup timeouts on unmount, don't cancel the fetch
    // This prevents premature cancellation on re-renders
    return () => {
      // Only clear timeout, don't cancel the fetch
      // This prevents premature cancellation on re-renders
      if (fetchAllDataRef.current?.loadingTimeout) {
        clearTimeout(fetchAllDataRef.current.loadingTimeout);
        fetchAllDataRef.current.loadingTimeout = null;
      }
    };
    // Only depend on session ID to prevent re-fetching when session object reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id || session?.trainerId]);

  // Validation functions
  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Both dates are required';
    if (new Date(startDate) >= new Date(endDate)) return 'End date must be after start date';
    if (new Date(startDate) < new Date().setHours(0, 0, 0, 0)) return 'Start date cannot be in the past';
    return null;
  };

  const validatePercentage = (value) => {
    if (isNaN(value) || value <= 0 || value > 100) return 'Must be between 1 and 100';
    return null;
  };

  const validatePrice = (value) => {
    if (isNaN(value) || value <= 0) return 'Must be a positive number';
    return null;
  };

  // Confirmation modal functions
  const showDeleteConfirmation = async (item, type) => {
    setDeleteItem(item);
    setDeleteType(type);
    
    // Check dependencies for programs
    if (type === 'program') {
      const dependencies = await checkProgramDependencies(item._id);
      if (dependencies && dependencies.hasDependencies) {
        setDeleteItem({ ...item, dependencies: dependencies.dependencies, errorMessage: dependencies.message });
      }
    }
    
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem || !deleteType) return;

    try {
      setIsLoading(true);
      let endpoint = '';
      
      switch (deleteType) {
        case 'promo':
          endpoint = `/api/fee-plan/promo-codes/${deleteItem._id}`;
          await HttpInterceptor.delete(endpoint);
          setPromoCodes(promoCodes.filter(promo => promo._id !== deleteItem._id));
          toast.success('Promo code deleted successfully');
          break;
        case 'discount':
          endpoint = `/api/fee-plan/discounts/${deleteItem._id}`;
          await HttpInterceptor.delete(endpoint);
          setDiscounts(discounts.filter(discount => discount._id !== deleteItem._id));
          toast.success('Discount deleted successfully');
          break;
        case 'installment':
          endpoint = `/api/fee-plan/installments/${deleteItem._id}`;
          await HttpInterceptor.delete(endpoint);
          setInstallmentPlans(installmentPlans.filter(plan => plan._id !== deleteItem._id));
          toast.success('Installment plan deleted successfully');
          break;
        case 'scholarship':
          endpoint = `/api/fee-plan/scholarship-offers/${deleteItem._id}`;
          await HttpInterceptor.delete(endpoint);
          setScholarshipOffers(scholarshipOffers.filter(offer => offer._id !== deleteItem._id));
          toast.success('Scholarship offer deleted successfully');
          break;
        case 'program':
          await deleteProgram(deleteItem._id, showForceDeleteConfirm);
          break;
        default:
          throw new Error('Invalid delete type');
      }
      
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || `Failed to delete ${deleteType}`);
      console.error(`Delete ${deleteType} error:`, error);
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setShowForceDeleteConfirm(false);
      setDeleteItem(null);
      setDeleteType('');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteItem(null);
    setDeleteType('');
  };

  // Edit functions
  const startEditPromo = (promo) => {
    setEditingPromo({
      _id: promo._id,
      code: promo.code,
      discount: promo.discount,
      validFrom: promo.validFrom.split('T')[0],
      validUntil: promo.validUntil.split('T')[0],
      programIds: promo.programIds,
      hasPriority: promo.hasPriority,
    });
    setSelectedProgram(promo.programIds[0] || '');
    fetchProgramDetails(promo.programIds[0] || '');
  };

  const cancelEditPromo = () => {
    setEditingPromo(null);
    setSelectedProgram('');
    setSelectedProgramDetails(null);
  };

  const updatePromoCode = async () => {
    if (!editingPromo) return;

    const dateError = validateDateRange(editingPromo.validFrom, editingPromo.validUntil);
    if (dateError) {
      toast.warning(dateError);
      return;
    }

    const discountError = validatePercentage(editingPromo.discount);
    if (discountError) {
      toast.warning(discountError);
      return;
    }

    try {
      setIsLoading(true);
      const response = await HttpInterceptor.put(`/api/fee-plan/promo-codes/${editingPromo._id}`, {
        code: editingPromo.code.toUpperCase(),
        discount: Number(editingPromo.discount),
        validFrom: editingPromo.validFrom,
        validUntil: editingPromo.validUntil,
        programIds: editingPromo.programIds,
        hasPriority: editingPromo.hasPriority,
      });

      setPromoCodes(promoCodes.map(promo => 
        promo._id === editingPromo._id 
          ? { ...response.data, programIds: response.data.programIds.map(id => id.toString()) }
          : promo
      ));
      
      toast.success('Promo code updated successfully');
      cancelEditPromo();
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to update promo code');
      console.error('Update promo code error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startEditDiscount = (discount) => {
    setEditingDiscount({
      _id: discount._id,
      name: discount.name,
      discount: discount.discount,
      validFrom: discount.validFrom.split('T')[0],
      validUntil: discount.validUntil.split('T')[0],
      programIds: discount.programIds,
      hasPriority: discount.hasPriority,
      selectionType: discount.selectionType || 'single',
    });
    setSelectedProgram(discount.programIds[0] || '');
    fetchProgramDetails(discount.programIds[0] || '');
  };

  const cancelEditDiscount = () => {
    setEditingDiscount(null);
    setSelectedProgram('');
    setSelectedProgramDetails(null);
  };

  const updateDiscount = async () => {
    if (!editingDiscount) return;

    const dateError = validateDateRange(editingDiscount.validFrom, editingDiscount.validUntil);
    if (dateError) {
      toast.warning(dateError);
      return;
    }

    const discountError = validatePercentage(editingDiscount.discount);
    if (discountError) {
      toast.warning(discountError);
      return;
    }

    try {
      setIsLoading(true);
      const response = await HttpInterceptor.put(`/api/fee-plan/discounts/${editingDiscount._id}`, {
        name: editingDiscount.name,
        discount: Number(editingDiscount.discount),
        validFrom: editingDiscount.validFrom,
        validUntil: editingDiscount.validUntil,
        programIds: editingDiscount.programIds,
        hasPriority: editingDiscount.hasPriority,
        selectionType: editingDiscount.selectionType,
      });

      setDiscounts(discounts.map(discount => 
        discount._id === editingDiscount._id 
          ? { ...response.data, programIds: response.data.programIds.map(id => id.toString()) }
          : discount
      ));
      
      toast.success('Discount updated successfully');
      cancelEditDiscount();
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to update discount');
      console.error('Update discount error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startEditInstallment = (installment) => {
    setEditingInstallment({
      _id: installment._id,
      name: installment.name,
      programId: installment.programId,
      numberOfInstallments: installment.intervals.length,
      intervals: installment.intervals.map(interval => ({
        dueDate: interval.dueDate,
        percentage: interval.percentage,
      })),
      isActive: installment.isActive,
    });
  };

  const cancelEditInstallment = () => {
    setEditingInstallment(null);
  };

  const updateInstallmentPlan = async () => {
    if (!editingInstallment) return;

    const totalPercentage = editingInstallment.intervals.reduce((sum, interval) => sum + Number(interval.percentage), 0);
    if (totalPercentage > 100) {
      toast.warning('Total percentage of all installments cannot exceed 100%');
      return;
    }

    try {
      setIsLoading(true);
      const response = await HttpInterceptor.put(`/api/fee-plan/installments/${editingInstallment._id}`, {
        name: editingInstallment.name,
        programId: editingInstallment.programId,
        numberOfInstallments: editingInstallment.numberOfInstallments,
        intervals: editingInstallment.intervals.map(interval => ({
          dueDate: interval.dueDate,
          percentage: Number(interval.percentage),
        })),
        isActive: editingInstallment.isActive,
      });

      setInstallmentPlans(installmentPlans.map(plan => 
        plan._id === editingInstallment._id ? response.data : plan
      ));
      
      toast.success('Installment plan updated successfully');
      cancelEditInstallment();
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to update installment plan');
      console.error('Update installment plan error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startEditScholarship = (scholarship) => {
    setEditingScholarship({
      _id: scholarship._id,
      name: scholarship.name,
      programId: scholarship.programId,
      discountPercentage: scholarship.discountPercentage,
      description: scholarship.description,
      isActive: scholarship.isActive,
    });
  };

  const cancelEditScholarship = () => {
    setEditingScholarship(null);
  };

  const updateScholarshipOffer = async () => {
    if (!editingScholarship) return;

    const discountError = validatePercentage(editingScholarship.discountPercentage);
    if (discountError) {
      toast.warning(discountError);
      return;
    }

    try {
      setIsLoading(true);
      const response = await HttpInterceptor.put(`/api/fee-plan/scholarship-offers/${editingScholarship._id}`, {
        name: editingScholarship.name,
        programId: editingScholarship.programId,
        discountPercentage: Number(editingScholarship.discountPercentage),
        description: editingScholarship.description,
        isActive: editingScholarship.isActive,
      });

      setScholarshipOffers(scholarshipOffers.map(offer => 
        offer._id === editingScholarship._id ? response.data : offer
      ));
      
      toast.success('Scholarship offer updated successfully');
      cancelEditScholarship();
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to update scholarship offer');
      console.error('Update scholarship offer error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete functions
  const deletePromoCode = async (id) => {
    try {
      setIsLoading(true);
      await HttpInterceptor.delete(`/api/fee-plan/promo-codes/${id}`);
      setPromoCodes(promoCodes.filter(promo => promo._id !== id));
      toast.success('Promo code deleted successfully');
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to delete promo code');
      console.error('Delete promo code error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDiscount = async (id) => {
    try {
      setIsLoading(true);
      await HttpInterceptor.delete(`/api/fee-plan/discounts/${id}`);
      setDiscounts(discounts.filter(discount => discount._id !== id));
      toast.success('Discount deleted successfully');
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to delete discount');
      console.error('Delete discount error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteInstallmentPlan = async (id) => {
    try {
      setIsLoading(true);
      await HttpInterceptor.delete(`/api/fee-plan/installments/${id}`);
      setInstallmentPlans(installmentPlans.filter(plan => plan._id !== id));
      toast.success('Installment plan deleted successfully');
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to delete installment plan');
      console.error('Delete installment plan error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePricing = async () => {
    if (!pricing.programId) {
      toast.warning('Please select a program');
      return;
    }
    
    const priceError = validatePrice(pricing.price);
    if (priceError) {
      toast.warning(priceError);
      return;
    }

    const price = Number(pricing.price);

    const selectedProgram = memoizedPrograms.find(p => p._id.toString() === pricing.programId.toString());
    if (!selectedProgram?.isActive) {
      toast.warning('Cannot set pricing for inactive program');
      return;
    }

    try {
      setIsLoading(true);
      const requestData = {
        programId: pricing.programId,
        price,
        trainerId: session?.trainerId || session?.id,
      };
      console.log('Sending pricing update request:', requestData);
      
      const response = await HttpInterceptor.post('/api/fee-plan/pricing', requestData);
      console.log('Pricing update response:', response.data);

      const updatedPricing = response.data?.data || response.data;
      if (!updatedPricing?.programId || updatedPricing.price === undefined) {
        throw new Error('Invalid pricing data received from server');
      }

      setPrograms(prevPrograms =>
        prevPrograms.map(program =>
          program._id.toString() === updatedPricing.programId.toString()
            ? {
                ...program,
                price: Number(updatedPricing.price),
                isActive: true,
              }
            : program
        )
      );

      toast.success('Pricing updated successfully');
      setPricing({
        programId: '',
        price: '',
      });
      fetchAllData();
    } catch (error) {
      console.error('Pricing update error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update pricing';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Program management functions
  const startEditProgram = (program) => {
    setPricing({
      programId: program._id,
      price: program.price?.toString() || program.currentPrice?.toString() || program.basePrice?.toString() || '',
    });
  };

  const checkProgramDependencies = async (programId) => {
    try {
      const response = await HttpInterceptor.get(`/api/programs/${programId}/dependencies`);
      return response.data;
    } catch (error) {
      console.error('Error checking dependencies:', error);
      return null;
    }
  };

  const deleteProgram = async (programId, force = false) => {
    try {
      const url = force ? `/api/programs/${programId}?force=true` : `/api/programs/${programId}`;
      const response = await HttpInterceptor.delete(url);
      if (response.status === 200) {
        toast.success('Program deleted successfully');
        // Update programs state by removing the deleted program
        setPrograms(prevPrograms => prevPrograms.filter(program => program._id !== programId));
        // Optionally refresh all data
        if (debouncedFetchAllDataRef.current) {
          debouncedFetchAllDataRef.current();
        }
      }
    } catch (error) {
      console.error('Delete program error:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to delete program';
      if (error.response?.data?.dependencies) {
        // Store dependency info for display in modal
        setDeleteItem({ ...deleteItem, dependencies: error.response.data.dependencies, errorMessage: errorMsg });
      } else {
        toast.error(errorMsg);
      }
      throw error; // Re-throw to handle in calling function
    }
  };

  const toggleProgramStatus = async (programId, currentStatus) => {
    setTogglingProgramStatus(true);
    try {
      const response = await HttpInterceptor.patch(`/api/programs/${programId}/toggle-status`);
      
      if (response.status === 200) {
        toast.success(`Program ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        // Update the state directly instead of fetching all programs again
        setPrograms(prevPrograms => 
          prevPrograms.map(program => 
            program._id === programId 
              ? { ...program, isActive: !currentStatus }
              : program
          )
        );
      }
    } catch (error) {
      console.error('Toggle program status error:', error);
      toast.error(error.response?.data?.message || 'Failed to toggle program status');
    } finally {
      setTogglingProgramStatus(false);
    }
  };

  const toggleInstallmentPlanStatus = async (planId, currentStatus) => {
    try {
      const response = await HttpInterceptor.patch(`/api/fee-plan/installments/${planId}/status`, {
        isActive: !currentStatus
      });
      
      if (response.status === 200) {
        toast.success(`Installment plan ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        // Update the state directly instead of just calling fetchInstallmentPlans
        setInstallmentPlans(prevPlans => 
          prevPlans.map(plan => 
            plan._id === planId 
              ? { ...plan, isActive: !currentStatus }
              : plan
          )
        );
      }
    } catch (error) {
      console.error('Toggle installment plan status error:', error);
      toast.error(error.response?.data?.message || 'Failed to toggle installment plan status');
    }
  };


  const isUniquePromoCode = (code) => {
    return !promoCodes.some(promo => promo.code.toLowerCase() === code.toLowerCase());
  };

  const togglePromoApplyToAll = () => {
    const applyToAll = !newPromo.applyToAll;
    setNewPromo({
      ...newPromo,
      applyToAll,
      programIds: applyToAll ? memoizedPrograms.filter(p => p.isActive).map(p => p._id.toString()) : [],
    });
  };

  const togglePromoProgramSelection = (programId) => {
    programId = programId.toString();
    let programIds;
    if (newPromo.programIds.includes(programId)) {
      programIds = newPromo.programIds.filter(id => id !== programId);
    } else {
      programIds = [...newPromo.programIds, programId];
    }

    setNewPromo({
      ...newPromo,
      programIds,
      applyToAll: programIds.length === memoizedPrograms.filter(p => p.isActive).length,
    });
  };

  const toggleDiscountApplyToAll = () => {
    const applyToAll = !newDiscount.applyToAll;
    setNewDiscount({
      ...newDiscount,
      applyToAll,
      programIds: applyToAll ? memoizedPrograms.filter(p => p.isActive).map(p => p._id.toString()) : [],
    });
  };

  const toggleDiscountProgramSelection = (programId) => {
    programId = programId.toString();
    let programIds;
    if (newDiscount.programIds.includes(programId)) {
      programIds = newDiscount.programIds.filter(id => id !== programId);
    } else {
      programIds = [...newDiscount.programIds, programId];
    }

    setNewDiscount({
      ...newDiscount,
      programIds,
      applyToAll: programIds.length === memoizedPrograms.filter(p => p.isActive).length,
    });
  };

  const toggleStackingPreference = async () => {
    try {
      setIsLoading(true);
      const newStackingValue = !stackingEnabled;
      await HttpInterceptor.post('/api/fee-plan/stacking-preference', {
        stackingEnabled: newStackingValue,
      });
      setStackingEnabled(newStackingValue);
      toast.success(`Discount stacking ${newStackingValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to update stacking preference:', error);
      toast.error('Failed to update stacking preference');
    } finally {
      setIsLoading(false);
    }
  };

  const addPromoCode = async () => {
    if (!selectedProgram) {
      toast.warning('Please select a program first');
      return;
    }
    
    if (!newPromo.code || !newPromo.discount || !newPromo.validFrom || !newPromo.validUntil) {
      toast.warning('Please fill all required fields');
      return;
    }

    const dateError = validateDateRange(newPromo.validFrom, newPromo.validUntil);
    if (dateError) {
      toast.warning(dateError);
      return;
    }

    const discountError = validatePercentage(newPromo.discount);
    if (discountError) {
      toast.warning(discountError);
      return;
    }

    if (!isUniquePromoCode(newPromo.code)) {
      toast.warning('This promo code already exists');
      return;
    }

    const inactivePrograms = newPromo.programIds.filter(id => !memoizedPrograms.find(p => p._id.toString() === id && p.isActive));
    if (inactivePrograms.length > 0) {
      toast.warning('Cannot create promo code for inactive programs');
      return;
    }

    try {
      setIsLoading(true);
      const response = await HttpInterceptor.post('/api/fee-plan/promo-codes', {
        code: newPromo.code.toUpperCase(),
        discount: Number(newPromo.discount),
        validFrom: newPromo.validFrom,
        validUntil: newPromo.validUntil,
        programIds: [selectedProgram],
        hasPriority: newPromo.hasPriority,
        trainerId: session?.trainerId || session?.id,
      });

      const newPromoCode = response.data?.data || response.data;
      setPromoCodes([
        ...promoCodes,
        {
          ...newPromoCode,
          programIds: newPromoCode.programIds.map(id => id.toString()),
        },
      ]);
      setNewPromo({
        code: '',
        discount: '',
        validFrom: '',
        validUntil: '',
        programIds: [],
        applyToAll: false,
        hasPriority: false,
      });
      setSelectedProgram('');
      setSelectedProgramDetails(null);
      toast.success('Promo code created successfully');
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to create promo code');
      console.error('Promo code error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addDiscount = async () => {
    if (!selectedProgram) {
      toast.warning('Please select a program first');
      return;
    }
    
    if (!newDiscount.name || !newDiscount.discount || !newDiscount.validFrom || !newDiscount.validUntil) {
      toast.warning('Please fill all required fields');
      return;
    }

    const dateError = validateDateRange(newDiscount.validFrom, newDiscount.validUntil);
    if (dateError) {
      toast.warning(dateError);
      return;
    }

    const discountError = validatePercentage(newDiscount.discount);
    if (discountError) {
      toast.warning(discountError);
      return;
    }

    const inactivePrograms = newDiscount.programIds.filter(id => !memoizedPrograms.find(p => p._id.toString() === id && p.isActive));
    if (inactivePrograms.length > 0) {
      toast.warning('Cannot create discount for inactive programs');
      return;
    }

    try {
      setIsLoading(true);
      const response = await HttpInterceptor.post('/api/fee-plan/discounts', {
        name: newDiscount.name,
        discount: Number(newDiscount.discount),
        validFrom: newDiscount.validFrom,
        validUntil: newDiscount.validUntil,
        programIds: [selectedProgram],
        hasPriority: newDiscount.hasPriority,
        selectionType: 'single',
        trainerId: session?.trainerId || session?.id,
      });

      const newDiscountItem = response.data?.data || response.data;
      setDiscounts([
        ...discounts,
        {
          ...newDiscountItem,
          programIds: newDiscountItem.programIds.map(id => id.toString()),
        },
      ]);
      setNewDiscount({
        name: '',
        discount: '',
        validFrom: '',
        validUntil: '',
        programIds: [],
        applyToAll: false,
        hasPriority: false,
      });
      setSelectedProgram('');
      setSelectedProgramDetails(null);
      toast.success('Discount created successfully');
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to create discount');
      console.error('Discount error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addInstallmentPlan = async () => {
    if (!newInstallment.name || !newInstallment.programId || newInstallment.intervals.length === 0) {
      toast.warning('Please fill all required fields and add at least one installment interval');
      return;
    }

    const totalPercentage = newInstallment.intervals.reduce((sum, interval) => sum + Number(interval.percentage), 0);
    if (totalPercentage > 100) {
      toast.warning('Total percentage of all installments cannot exceed 100%');
      return;
    }

    const hasValidDueDates = newInstallment.intervals.every(interval => new Date(interval.dueDate) > new Date());
    if (!hasValidDueDates) {
      toast.warning('All due dates must be in the future');
      return;
    }

    try {
      setIsLoading(true);
      const response = await HttpInterceptor.post('/api/fee-plan/installments', {
        name: newInstallment.name,
        programId: newInstallment.programId,
        numberOfInstallments: newInstallment.numberOfInstallments,
        intervals: newInstallment.intervals.map(interval => ({
          dueDate: interval.dueDate,
          percentage: Number(interval.percentage),
        })),
        isActive: newInstallment.isActive,
        trainerId: session?.trainerId || session?.id,
      });

      const newPlan = response.data?.data || response.data;
      setInstallmentPlans([...installmentPlans, newPlan]);
      setNewInstallment({
        name: '',
        programId: '',
        numberOfInstallments: 2,
        intervals: [
          { dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), percentage: 50 },
          { dueDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(), percentage: 50 },
        ],
        isActive: true,
      });
      toast.success('Installment plan created successfully');
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to create installment plan');
      console.error('Installment plan error:', error.response?.data || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const addScholarshipOffer = async () => {
    if (!newScholarshipOffer.name || !newScholarshipOffer.programId || !newScholarshipOffer.discountPercentage) {
      toast.warning('Please fill all required fields');
      return;
    }

    const discountError = validatePercentage(newScholarshipOffer.discountPercentage);
    if (discountError) {
      toast.warning(discountError);
      return;
    }

    try {
      setIsLoading(true);
      const response = await HttpInterceptor.post('/api/fee-plan/scholarship-offers', {
        name: newScholarshipOffer.name,
        programId: newScholarshipOffer.programId,
        discountPercentage: Number(newScholarshipOffer.discountPercentage),
        description: newScholarshipOffer.description,
        isActive: newScholarshipOffer.isActive,
        trainerId: session?.trainerId || session?.id,
      });

      const newOffer = response.data?.data || response.data;
      setScholarshipOffers([...scholarshipOffers, newOffer]);
      setNewScholarshipOffer({
        name: '',
        programId: '',
        discountPercentage: '',
        description: '',
        isActive: true,
      });
      toast.success('Scholarship offer created successfully');
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to create scholarship offer');
      console.error('Scholarship offer error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteScholarshipOffer = async (id) => {
    try {
      setIsLoading(true);
      await HttpInterceptor.delete(`/api/fee-plan/scholarship-offers/${id}`);
      setScholarshipOffers(scholarshipOffers.filter(offer => offer._id !== id));
      toast.success('Scholarship offer deleted successfully');
      fetchAllData();
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || 'Failed to delete scholarship offer');
      console.error('Delete scholarship offer error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleScholarshipOfferStatus = async (id, isActive) => {
    try {
      setIsLoading(true);
      await HttpInterceptor.put(`/api/fee-plan/scholarship-offers/${id}/status`, { isActive });
      setScholarshipOffers(scholarshipOffers.map(offer => (offer._id === id ? { ...offer, isActive } : offer)));
      toast.success(`Scholarship offer ${isActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || `Failed to update scholarship offer status`);
      console.error('Scholarship offer status update error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateScholarshipApplicationStatus = async (id, status) => {
    try {
      setIsLoading(true);
      await HttpInterceptor.put(`/api/fee-plan/scholarship-applications/${id}`, { status });
      setScholarshipApplications(scholarshipApplications.map(app => (app._id === id ? { ...app, status } : app)));
      toast.success(`Scholarship application ${status}`);
    } catch (error) {
      setError(error);
      toast.error(error.response?.data?.message || `Failed to ${status} scholarship application`);
      console.error('Scholarship application status update error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateInstallmentInterval = (index, field, value) => {
    const newIntervals = [...newInstallment.intervals];
    // Calculate date relative to program's effectiveDate if available, otherwise use current date
    const baseDate = selectedProgramDetails?.effectiveDate 
      ? new Date(selectedProgramDetails.effectiveDate) 
      : new Date();
    
    newIntervals[index] = {
      ...newIntervals[index],
      [field]: field === 'dueDate' 
        ? new Date(baseDate.getTime() + Number(value) * 24 * 60 * 60 * 1000).toISOString() 
        : Number(value),
    };
    setNewInstallment({
      ...newInstallment,
      intervals: newIntervals,
    });
  };

  const addInstallmentInterval = () => {
    // Calculate date relative to program's effectiveDate if available, otherwise use current date
    const baseDate = selectedProgramDetails?.effectiveDate 
      ? new Date(selectedProgramDetails.effectiveDate) 
      : new Date();
    const daysOffset = (newInstallment.intervals.length + 1) * 30;
    
    const newNum = newInstallment.intervals.length + 1;
    // Recalculate percentages evenly for all installments
    const basePercentage = Math.floor(100 / newNum);
    const remainder = 100 % newNum;
    
    const updatedIntervals = newInstallment.intervals.map((interval, i) => ({
      ...interval,
      percentage: i < remainder ? basePercentage + 1 : basePercentage,
    }));
    
    updatedIntervals.push({
      dueDate: new Date(baseDate.getTime() + daysOffset * 24 * 60 * 60 * 1000).toISOString(),
      percentage: (newInstallment.intervals.length < remainder) ? basePercentage + 1 : basePercentage,
    });
    
    setNewInstallment({
      ...newInstallment,
      intervals: updatedIntervals,
      numberOfInstallments: newNum,
    });
  };

  const removeInstallmentInterval = (index) => {
    const newIntervals = [...newInstallment.intervals];
    newIntervals.splice(index, 1);
    const newNum = newIntervals.length;
    
    // Recalculate percentages evenly for remaining installments
    if (newNum > 0) {
      const basePercentage = Math.floor(100 / newNum);
      const remainder = 100 % newNum;
      
      const updatedIntervals = newIntervals.map((interval, i) => ({
        ...interval,
        percentage: i < remainder ? basePercentage + 1 : basePercentage,
      }));
      
      setNewInstallment({
        ...newInstallment,
        intervals: updatedIntervals,
        numberOfInstallments: newNum,
      });
    } else {
      setNewInstallment({
        ...newInstallment,
        intervals: [],
        numberOfInstallments: 0,
      });
    }
  };

  const getProgramName = (id) => {
    return programNameCache.get(id.toString()) || 'Unknown Program';
  };

  const getStudentName = (studentId) => {
    return `Student ${studentId.substring(0, 6)}`;
  };

  const dateTimeSettings = useDateTimeSettings();
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateUtil(dateString, dateTimeSettings.dateFormat);
  };

  const isExpired = (dateString) => {
    if (!dateString) return true;
    const expiryDate = new Date(dateString);
    expiryDate.setHours(23, 59, 59, 999); // Normalize to end of day
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Normalize to start of day
    return expiryDate < now;
  };

  // Helper function to check if a date is upcoming (normalized date comparison)
  const isUpcoming = (dateString) => {
    if (!dateString) return false;
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0); // Normalize to start of day
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Normalize to start of day
    return targetDate > now;
  };

  const DashboardTabPanel = (
    <div>
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-6">Pricing & Discounts Overview</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4">
                <FaChartBar className="text-xl" />
              </div>
              <div>
                <h3 className="text-gray-500 text-sm font-medium">Active Programs</h3>
                <p className="text-2xl font-bold text-orange-600">{dashboardStats.activePrograms} / {dashboardStats.totalPrograms}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-amber-100 text-amber-600 mr-4">
                <FaCalendarAlt className="text-xl" />
              </div>
              <div>
                <h3 className="text-gray-500 text-sm font-medium">Active Promos</h3>
                <p className="text-2xl font-bold text-amber-600">{dashboardStats.activePromos}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-4">
                <FaCalendarAlt className="text-xl" />
              </div>
              <div>
                <h3 className="text-gray-500 text-sm font-medium">Active Discounts</h3>
                <p className="text-2xl font-bold text-yellow-600">{dashboardStats.activeDiscounts}</p>
              </div>
            </div>
          </div>

          {!isConsultant && (
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4">
                  <FaUserGraduate className="text-xl" />
                </div>
                <div>
                  <h3 className="text-gray-500 text-sm font-medium">Pending Scholarships</h3>
                  <p className="text-2xl font-bold text-orange-600">{dashboardStats.pendingApplications}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-medium mb-4">Recent Promo Codes</h3>
            <div className="space-y-3">
              {promoCodes.slice(0, 3).map(promo => (
                <div key={promo._id} className="border-b pb-3 last:border-b-0 last:pb-0">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{promo.code}</h4>
                      <p className="text-sm text-gray-600">{promo.discount}% discount</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isExpired(promo.validUntil)
                        ? 'bg-red-100 text-red-800'
                        : isUpcoming(promo.validFrom)
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {isExpired(promo.validUntil)
                        ? 'Expired'
                        : isUpcoming(promo.validFrom)
                        ? 'Upcoming'
                        : 'Active'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Valid: {formatDate(promo.validFrom)} - {formatDate(promo.validUntil)}
                  </p>
                </div>
              ))}
              {promoCodes.length === 0 && (
                <p className="text-gray-500 text-center py-4">No promo codes found</p>
              )}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-medium mb-4">Recent Scholarship Applications</h3>
            <div className="space-y-3">
              {scholarshipApplications.slice(0, 3).map(app => (
                <div key={app._id} className="border-b pb-3 last:border-b-0 last:pb-0">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{getStudentName(app.studentId)}</h4>
                      <p className="text-sm text-gray-600">{getProgramName(app.programId)}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      app.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : app.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                    Applied: {formatDate(app.createdAt)}
                  </p>
                </div>
              ))}
              {scholarshipApplications.length === 0 && (
                <p className="text-gray-500 text-center py-4">No applications found</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const PricingTabPanel = (
    <div>
      <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4'}`}>
        {/* Update Program Pricing Form */}
        <div className={`mb-6 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-4 sm:p-6 rounded-2xl' : 'p-4 rounded-lg'} relative overflow-visible group`}>
          {isPWA && (
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100/30 to-amber-100/20 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
            </div>
          )}
          <div className={`flex items-center gap-2 ${isPWA ? 'mb-4' : 'mb-3'} relative z-10`}>
            {isPWA && (
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md">
                <FaRupeeSign className="text-white text-base" />
              </div>
            )}
            <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} ${isPWA ? 'font-bold text-orange-800' : 'font-medium text-gray-900'}`}>Update Program Pricing</h3>
            <InfoTooltip
              width="w-72"
              content={
                <>
                  <div className="font-semibold mb-1">Program Pricing</div>
                  <p className="text-gray-300">
                    Set the base price for your training programs. This price will be used as the starting point for all discount calculations. Students will see this price before any discounts, promo codes, or scholarships are applied.
                  </p>
                </>
              }
            >
              <FaInfoCircle className={`${isPWA ? 'text-orange-500 hover:text-orange-700' : 'text-gray-400 hover:text-gray-600'}`} size={16} />
            </InfoTooltip>
          </div>
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-4 ${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Program*
                <InfoTooltip
                  width="w-56"
                  content="Select the program you want to update pricing for. Only active programs are shown."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <select
                value={pricing.programId}
                onChange={(e) => setPricing({ ...pricing, programId: e.target.value })}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                required
              >
                <option value="">Select Program</option>
                {memoizedPrograms.filter(p => p.isActive).map(program => (
                  <option key={program._id} value={program._id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Price (₹)*
                <InfoTooltip
                  width="w-56"
                  content="Enter the base price for this program in Indian Rupees. This is the price before any discounts are applied."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="number"
                value={pricing.price}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || validatePrice(value) === null) {
                    setPricing({ ...pricing, price: value });
                  }
                }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                placeholder="5000"
                min="0"
                required
              />
              {pricing.price && validatePrice(pricing.price) && (
                <p className={`${isPWA ? 'text-red-600 font-medium' : 'text-red-500'} text-xs mt-1.5`}>{validatePrice(pricing.price)}</p>
              )}
            </div>
          </div>
          <button
            onClick={updatePricing}
            disabled={!pricing.programId || !pricing.price || isLoading}
            className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg border border-orange-400/50 font-bold' : 'bg-orange-600 text-white px-4 py-2 hover:bg-orange-700'} rounded-lg transition-all duration-200 ${
              !pricing.programId || !pricing.price || isLoading ? 'opacity-50 cursor-not-allowed' : ''
            } relative z-10`}
          >
            <FaPlus className="inline mr-1.5" />
            {isLoading ? 'Updating...' : 'Update Pricing'}
          </button>
        </div>

          {isLoading ? (
          <div className={`text-center py-8 ${isPWA ? 'bg-gradient-to-br from-orange-50/30 via-amber-50/20 to-yellow-50/10 rounded-xl border border-orange-200/50' : ''}`}>
              <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
              <p className={`mt-2 ${isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}`}>Loading pricing...</p>
            </div>
        ) : memoizedPrograms.length > 0 ? (
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'}`}>
            {memoizedPrograms.map(program => (
              <div key={program._id} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl hover:shadow-2xl hover:border-orange-300/80' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'} rounded-xl transition-all duration-300 ${isPWA ? 'p-4 sm:p-5' : 'p-5'} relative overflow-hidden group`}>
                {isPWA && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`flex items-start justify-between ${isPWA ? 'mb-3' : 'mb-3'} relative z-10`}>
                  <div className="flex-1">
                    <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-900'} ${isPWA ? 'mb-2' : 'mb-2'}`}>{program.name}</h3>
                        <span
                      className={`inline-block ${isPWA ? 'px-2.5 py-1 text-[10px] sm:text-xs' : 'px-2.5 py-1 text-xs'} font-bold rounded-full cursor-help shadow-sm ${
                            program.isActive 
                              ? isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50' : 'bg-green-100 text-green-800'
                              : isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50' : 'bg-red-100 text-red-800'
                          }`}
                          title={program.isActive ? 'This program is currently active and available for enrollment' : 'This program is inactive and not available for enrollment'}
                        >
                          {program.isActive ? 'Active' : 'Inactive'}
                        </span>
                  </div>
                </div>
                <div className={`mt-4 pt-4 ${isPWA ? 'border-t-2 border-orange-200/50' : 'border-t border-gray-200'} relative z-10`}>
                  <div className={`flex items-baseline justify-between ${isPWA ? 'mb-4' : 'mb-4'}`}>
                    <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 font-bold' : 'text-gray-600'} flex items-center gap-1`} title="Base price for this program before any discounts">
                      Price
                      <FaInfoCircle className={`${isPWA ? 'text-orange-500' : 'text-gray-400'} cursor-help`} size={10} />
                    </span>
                    <span className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-600' : 'text-gray-900'}`} title="This is the base price students see before discounts, promo codes, or scholarships are applied">
                      {(() => {
                        const price = program.price ?? program.currentPrice ?? program.basePrice;
                        return price !== null && price !== undefined ? formatCurrency(price) : 'Not Set';
                      })()}
                    </span>
                  </div>
                  <div className={`flex items-center justify-end ${isPWA ? 'gap-2' : 'space-x-2'} pt-3 ${isPWA ? 'border-t border-orange-200/50' : 'border-t border-gray-100'}`}>
                        <button
                          onClick={() => toggleProgramStatus(program._id, program.isActive)}
                          disabled={togglingProgramStatus}
                      className={`${program.isActive 
                        ? isPWA ? 'text-green-600 hover:text-green-800 hover:bg-green-50/80 border border-green-200/50' : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                        : isPWA ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-50/80 border border-gray-200/50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'} ${togglingProgramStatus ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg shadow-sm' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title={program.isActive ? 'Program is Active' : 'Activate Program'}
                        >
                      {program.isActive ? <FaToggleOn size={isPWA ? 16 : 18} /> : <FaToggleOff size={isPWA ? 16 : 18} />}
                        </button>
                        <button
                          onClick={() => startEditProgram(program)}
                          disabled={isLoading}
                      className={`${isPWA ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50/80 border border-orange-200/50 shadow-sm' : 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Edit Pricing"
                        >
                      <FaEdit size={isPWA ? 14 : 16} />
                        </button>
                        <button
                          onClick={() => showDeleteConfirmation(program, 'program')}
                          disabled={isLoading}
                      className={`${isPWA ? 'text-red-600 hover:text-red-800 hover:bg-red-50/80 border border-red-200/50 shadow-sm' : 'text-red-600 hover:text-red-800 hover:bg-red-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Delete Program"
                        >
                      <FaTrash size={isPWA ? 14 : 16} />
                        </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`text-center py-12 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 rounded-xl' : 'bg-gray-50 rounded-lg border border-gray-200'}`}>
            <p className={isPWA ? 'text-orange-700 font-medium' : 'text-gray-500'}>No programs found</p>
          </div>
        )}
      </div>
    </div>
  );

  const PromoCodesTabPanel = (
    <div>
      <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4'}`}>
        {/* Header Section */}
        <div className={`${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-white border border-gray-200'} ${isPWA ? 'p-4 sm:p-5 rounded-2xl mb-4' : 'mb-4'} relative overflow-visible group`}>
          {isPWA && (
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100/30 to-amber-100/20 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
            </div>
          )}
          <div className={`flex ${isPWA ? 'flex-col sm:flex-row' : 'flex-row'} ${isPWA ? 'gap-4' : ''} justify-between items-${isPWA ? 'start sm:items-center' : 'center'} relative z-10`}>
            <div className="flex items-center gap-2">
              {isPWA && (
                <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md">
                  <FaTags className="text-white text-base" />
                </div>
              )}
              <h2 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-xl'} ${isPWA ? 'font-bold text-orange-800' : 'font-semibold text-gray-900'}`}>Promo Codes</h2>
              <InfoTooltip
                width="w-72"
                content={
                  <>
                    <div className="font-semibold mb-1">Promo Codes</div>
                    <p className="text-gray-300">
                      Create promotional codes that students can use to get discounts on specific programs. Promo codes can have validity periods and priority settings. When discount stacking is enabled, promo codes can be combined with other discounts.
                    </p>
                  </>
                }
              >
                <FaInfoCircle className={`${isPWA ? 'text-orange-500 hover:text-orange-700' : 'text-gray-400 hover:text-gray-600'}`} size={16} />
              </InfoTooltip>
            </div>
            <div className={`flex items-center ${isPWA ? 'gap-2 sm:gap-3' : ''}`}>
              <div className="flex items-center gap-2 mr-2">
                <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 font-bold' : 'text-gray-600'}`}>Discount Stacking:</span>
                <InfoTooltip
                  width="w-64"
                  content={
                    <>
                      <div className="font-semibold mb-1">Discount Stacking</div>
                      <p className="text-gray-300">
                        When enabled, multiple discounts (promo codes, discounts, and scholarships) can be combined and applied together to provide maximum savings. When disabled, only the best single discount will be applied.
                      </p>
                    </>
                  }
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-500 hover:text-orange-700' : 'text-gray-400 hover:text-gray-600'}`} size={16} />
                </InfoTooltip>
              </div>
              <button
                onClick={toggleStackingPreference}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  stackingEnabled 
                    ? isPWA ? 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-md' : 'bg-green-500'
                    : isPWA ? 'bg-gray-300 border border-gray-400' : 'bg-gray-200'
                }`}
                disabled={isLoading}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                    stackingEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`ml-2 ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 font-bold' : 'text-gray-600'}`}>{stackingEnabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
        </div>

        {/* Create/Edit Promo Code Form */}
        <div className={`mb-6 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-4 sm:p-6 rounded-2xl' : 'p-4 rounded-lg'} relative overflow-visible group`}>
          {isPWA && (
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100/30 to-amber-100/20 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
            </div>
          )}
          <div className={`flex items-center gap-2 ${isPWA ? 'mb-4' : 'mb-3'} relative z-10`}>
            {isPWA && (
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md">
                <FaTags className="text-white text-base" />
              </div>
            )}
            <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} ${isPWA ? 'font-bold text-orange-800' : 'font-medium text-gray-900'}`}>
              {editingPromo ? 'Edit Promo Code' : 'Create New Promo Code'}
            </h3>
          </div>
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-4'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} ${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Promo Code*
                <InfoTooltip
                  width="w-56"
                  content="Enter a unique code (e.g., SUMMER20) that students will use at checkout to apply this discount."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="text"
                  value={editingPromo ? editingPromo.code : newPromo.code}
                  onChange={(e) => {
                    if (editingPromo) {
                      setEditingPromo({ ...editingPromo, code: e.target.value });
                    } else {
                      setNewPromo({ ...newPromo, code: e.target.value });
                    }
                  }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                placeholder="SUMMER20"
                required
              />
            </div>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Discount (%)*
                <InfoTooltip
                  width="w-56"
                  content="Enter the discount percentage (1-100). This percentage will be applied to the program's base price."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="number"
                  value={editingPromo ? editingPromo.discount : newPromo.discount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || validatePercentage(value) === null) {
                      if (editingPromo) {
                        setEditingPromo({ ...editingPromo, discount: value });
                      } else {
                    setNewPromo({ ...newPromo, discount: value });
                      }
                  }
                }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                placeholder="20"
                min="1"
                max="100"
                required
              />
                {(editingPromo ? editingPromo.discount : newPromo.discount) && validatePercentage(editingPromo ? editingPromo.discount : newPromo.discount) && (
                  <p className={`${isPWA ? 'text-red-600 font-medium' : 'text-red-500'} text-xs mt-1.5`}>{validatePercentage(editingPromo ? editingPromo.discount : newPromo.discount)}</p>
              )}
            </div>
          </div>
          {/* Program Selection */}
          <div className={`${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-2'} flex items-center gap-1`}>
              Select Program*
              <InfoTooltip
                width="w-64"
                content="Select which program(s) this promo code applies to. The validity period will be based on the program's start date."
              >
                <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
              </InfoTooltip>
            </label>
            <select
              value={selectedProgram}
              onChange={(e) => handleProgramSelection(e.target.value)}
              className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
              required
            >
              <option value="">Select a program</option>
              {memoizedPrograms.filter(p => p.isActive).map(program => (
                <option key={program._id} value={program._id}>
                  {program.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Selection */}
          {selectedProgram && (
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} ${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Valid From*
                <InfoTooltip
                  width="w-56"
                  content="The date when this promo code becomes active. Must be on or after today's date so students enrolling before the program starts can use it."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
                {isLoadingProgramDetails ? (
                  <div className={`w-full ${isPWA ? 'border-2 border-orange-200' : 'border'} rounded-lg px-3 py-2 ${isPWA ? 'bg-orange-50/50' : 'bg-gray-100'}`}>
                    <div className={`animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
                    <span className={`ml-2 ${isPWA ? 'text-xs sm:text-sm text-orange-700' : 'text-sm text-gray-500'}`}>Loading program details...</span>
                  </div>
                ) : (
              <input
                type="date"
                value={editingPromo ? editingPromo.validFrom : newPromo.validFrom}
                onChange={(e) => {
                  if (editingPromo) {
                    setEditingPromo({ ...editingPromo, validFrom: e.target.value });
                  } else {
                    setNewPromo({ ...newPromo, validFrom: e.target.value });
                  }
                }}
                    min={getMinDate()}
                    className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all date-input`}
                required
              />
                )}
                {selectedProgramDetails && (
                  <p className={`${isPWA ? 'text-xs text-orange-700 font-medium' : 'text-xs text-gray-500'} mt-1.5`}>
                    Program starts: {formatDate(selectedProgramDetails.effectiveDate)}
                  </p>
                )}
            </div>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Valid Until*
                  <InfoTooltip
                    width="w-56"
                    content="The date when this promo code expires. Must be after the 'Valid From' date."
                  >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="date"
                value={editingPromo ? editingPromo.validUntil : newPromo.validUntil}
                onChange={(e) => {
                  if (editingPromo) {
                    setEditingPromo({ ...editingPromo, validUntil: e.target.value });
                  } else {
                    setNewPromo({ ...newPromo, validUntil: e.target.value });
                  }
                }}
                  min={(editingPromo ? editingPromo.validFrom : newPromo.validFrom) || getMinDate()}
                  className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all date-input`}
                required
                  disabled={!(editingPromo ? editingPromo.validFrom : newPromo.validFrom)}
              />
              {(editingPromo ? editingPromo.validFrom : newPromo.validFrom) && (editingPromo ? editingPromo.validUntil : newPromo.validUntil) && validateDateRange(editingPromo ? editingPromo.validFrom : newPromo.validFrom, editingPromo ? editingPromo.validUntil : newPromo.validUntil) && (
                <p className={`${isPWA ? 'text-red-600 font-medium' : 'text-red-500'} text-xs mt-1.5`}>{validateDateRange(editingPromo ? editingPromo.validFrom : newPromo.validFrom, editingPromo ? editingPromo.validUntil : newPromo.validUntil)}</p>
              )}
            </div>
          </div>
          )}
          <div className={`${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hasPriority"
                  checked={editingPromo ? editingPromo.hasPriority : newPromo.hasPriority}
                  onChange={(e) => {
                    if (editingPromo) {
                      setEditingPromo({ ...editingPromo, hasPriority: e.target.checked });
                    } else {
                      setNewPromo({ ...newPromo, hasPriority: e.target.checked });
                    }
                  }}
                  className={`h-4 w-4 ${isPWA ? 'text-orange-600 focus:ring-orange-500 border-orange-300' : 'text-orange-600 focus:ring-orange-500 border-gray-300'} rounded`}
                />
                <label htmlFor="hasPriority" className={`ml-2 block ${isPWA ? 'text-xs sm:text-sm text-orange-800 font-medium' : 'text-sm text-gray-900'} flex items-center gap-1`}>
                  Has priority over other discounts
                  <InfoTooltip
                    width="w-64"
                    content="When enabled, this promo code will take precedence over other discounts when multiple discounts are available. Useful for special promotions."
                  >
                    <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                  </InfoTooltip>
                </label>
              </div>
                  </div>
          <div className={`flex ${isPWA ? 'flex-col sm:flex-row gap-3' : 'space-x-2'} relative z-10`}>
          <button
              onClick={editingPromo ? updatePromoCode : addPromoCode}
            disabled={
                !selectedProgram || 
                !(editingPromo ? editingPromo.code : newPromo.code) || 
                !(editingPromo ? editingPromo.discount : newPromo.discount) || 
                !(editingPromo ? editingPromo.validFrom : newPromo.validFrom) || 
                !(editingPromo ? editingPromo.validUntil : newPromo.validUntil) || 
                isLoading || 
                validatePercentage(editingPromo ? editingPromo.discount : newPromo.discount) || 
                validateDateRange(editingPromo ? editingPromo.validFrom : newPromo.validFrom, editingPromo ? editingPromo.validUntil : newPromo.validUntil)
            }
            className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg border border-orange-400/50 font-bold' : 'bg-orange-600 text-white px-4 py-2 hover:bg-orange-700 shadow-sm'} rounded-lg transition-all duration-200 ${
                !selectedProgram || 
                !(editingPromo ? editingPromo.code : newPromo.code) || 
                !(editingPromo ? editingPromo.discount : newPromo.discount) || 
                !(editingPromo ? editingPromo.validFrom : newPromo.validFrom) || 
                !(editingPromo ? editingPromo.validUntil : newPromo.validUntil) || 
                isLoading || 
                validatePercentage(editingPromo ? editingPromo.discount : newPromo.discount) || 
                validateDateRange(editingPromo ? editingPromo.validFrom : newPromo.validFrom, editingPromo ? editingPromo.validUntil : newPromo.validUntil)
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
              {editingPromo ? <FaCheck className="inline mr-1.5" /> : <FaPlus className="inline mr-1.5" />}
              {isLoading ? (editingPromo ? 'Updating...' : 'Creating...') : (editingPromo ? 'Update Promo Code' : 'Create Promo Code')}
          </button>
            {editingPromo && (
              <button
                onClick={cancelEditPromo}
                disabled={isLoading}
                className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white shadow-md hover:shadow-lg border border-gray-300/50 font-bold' : 'bg-gray-500 text-white px-4 py-2 hover:bg-gray-600'} rounded-lg transition-all duration-200`}
              >
                <FaTimes className="inline mr-1.5" />
                Cancel
              </button>
            )}
          </div>
        </div>

          {isLoading ? (
          <div className={`text-center py-8 ${isPWA ? 'bg-gradient-to-br from-orange-50/30 via-amber-50/20 to-yellow-50/10 rounded-xl border border-orange-200/50' : ''}`}>
              <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
              <p className={`mt-2 ${isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}`}>Loading promo codes...</p>
            </div>
        ) : promoCodes.length > 0 ? (
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'}`}>
            {promoCodes.map(promo => (
              <div key={promo._id} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl hover:shadow-2xl hover:border-orange-300/80' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'} rounded-xl transition-all duration-300 ${isPWA ? 'p-4 sm:p-5' : 'p-5'} relative overflow-hidden group`}>
                {isPWA && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`flex items-start justify-between ${isPWA ? 'mb-3' : 'mb-3'} relative z-10`}>
                  <div className="flex-1">
                    <div className={`flex items-center ${isPWA ? 'gap-2 flex-wrap' : 'gap-2'} ${isPWA ? 'mb-2' : 'mb-2'}`}>
                      <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-900'} font-mono`}>{promo.code}</h3>
                      <span
                        className={`inline-block ${isPWA ? 'px-2 py-1 text-[10px] sm:text-xs' : 'px-2.5 py-1 text-xs'} font-bold rounded-full cursor-help shadow-sm ${
                          promo.hasPriority 
                            ? isPWA ? 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-200/50' : 'bg-orange-100 text-orange-800'
                            : isPWA ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border border-gray-200/50' : 'bg-gray-100 text-gray-800'
                        }`}
                        title={promo.hasPriority ? 'This promo code has priority and will be applied over other discounts' : 'This promo code has normal priority'}
                      >
                        {promo.hasPriority ? 'High Priority' : 'Normal'}
                      </span>
                    </div>
                    <div className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-600' : 'text-orange-600'} ${isPWA ? 'mb-3' : 'mb-3'}`} title="Discount percentage applied when this promo code is used">{promo.discount}%</div>
                  </div>
                </div>
                <div className={`space-y-3 ${isPWA ? 'mb-4' : 'mb-4'} relative z-10`}>
                  <div>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} ${isPWA ? 'text-orange-700 font-bold mb-1.5' : 'text-gray-500 mb-1'} flex items-center gap-1`} title="Programs this promo code applies to">
                      Programs
                      <FaInfoCircle className={`${isPWA ? 'text-orange-500' : 'text-gray-400'} cursor-help`} size={10} />
                    </p>
                        <div className="flex flex-wrap gap-1">
                          {promo.programIds.length > 0 ? (
                            promo.programIds.map(programId => (
                              <span
                                key={programId}
                                className={`${isPWA ? 'text-[10px] sm:text-xs px-2 py-1 font-bold shadow-sm' : 'text-xs px-2 py-1'} rounded ${
                                  programNameCache.has(programId) 
                                    ? isPWA ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200/50' : 'bg-orange-100 text-orange-800'
                                    : isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50' : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {getProgramName(programId)}
                              </span>
                            ))
                          ) : (
                            <span className={`${isPWA ? 'text-red-600 font-bold' : 'text-red-600'} ${isPWA ? 'text-xs sm:text-sm' : 'text-xs'}`}>No valid programs</span>
                          )}
                        </div>
                  </div>
                  <div>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} ${isPWA ? 'text-orange-700 font-bold mb-1.5' : 'text-gray-500 mb-1'} flex items-center gap-1`} title="Date range when this promo code is valid">
                      Validity Period
                      <FaInfoCircle className={`${isPWA ? 'text-orange-500' : 'text-gray-400'} cursor-help`} size={10} />
                    </p>
                    <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-800 font-semibold' : 'text-gray-700'}`}>{formatDate(promo.validFrom)} - {formatDate(promo.validUntil)}</p>
                  </div>
                  <div>
                    <span
                      className={`inline-block ${isPWA ? 'px-2.5 py-1 text-[10px] sm:text-xs font-bold shadow-sm' : 'px-2.5 py-1 text-xs font-medium'} rounded-full cursor-help ${
                        isExpired(promo.validUntil)
                          ? isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50' : 'bg-red-100 text-red-800'
                          : isUpcoming(promo.validFrom)
                          ? isPWA ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border border-yellow-200/50' : 'bg-yellow-100 text-yellow-800'
                          : isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50' : 'bg-green-100 text-green-800'
                      }`}
                      title={isExpired(promo.validUntil) ? 'This promo code has expired' : isUpcoming(promo.validFrom) ? 'This promo code is not yet active' : 'This promo code is currently active'}
                    >
                      {isExpired(promo.validUntil)
                        ? 'Expired'
                        : isUpcoming(promo.validFrom)
                        ? 'Upcoming'
                        : 'Active'}
                    </span>
                  </div>
                </div>
                <div className={`flex items-center justify-end ${isPWA ? 'gap-2' : 'space-x-2'} pt-3 ${isPWA ? 'border-t border-orange-200/50' : 'border-t border-gray-100'} relative z-10`}>
                        <button
                          onClick={() => startEditPromo(promo)}
                          disabled={isLoading}
                    className={`${isPWA ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50/80 border border-orange-200/50 shadow-sm' : 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Edit"
                        >
                    <FaEdit size={isPWA ? 14 : 16} />
                        </button>
                        <button
                          onClick={() => showDeleteConfirmation(promo, 'promo')}
                          disabled={isLoading}
                    className={`${isPWA ? 'text-red-600 hover:text-red-800 hover:bg-red-50/80 border border-red-200/50 shadow-sm' : 'text-red-600 hover:text-red-800 hover:bg-red-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Delete"
                        >
                    <FaTrash size={isPWA ? 14 : 16} />
                        </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`text-center py-12 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 rounded-xl' : 'bg-gray-50 rounded-lg border border-gray-200'}`}>
            <p className={isPWA ? 'text-orange-700 font-medium' : 'text-gray-500'}>No promo codes found</p>
          </div>
        )}
      </div>
    </div>
  );

  const DiscountsTabPanel = (
    <div>
      <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4'}`}>
        {/* Create/Edit Discount Form */}
        <div className={`mb-6 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-4 sm:p-6 rounded-2xl' : 'p-4 rounded-lg'} relative overflow-visible group`}>
          {isPWA && (
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100/30 to-amber-100/20 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
            </div>
          )}
          <div className={`flex items-center gap-2 ${isPWA ? 'mb-4' : 'mb-3'} relative z-10`}>
            {isPWA && (
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md">
                <FaPercent className="text-white text-base" />
              </div>
            )}
            <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} ${isPWA ? 'font-bold text-orange-800' : 'font-medium text-gray-900'}`}>
            {editingDiscount ? 'Edit Discount' : 'Create New Discount'}
          </h3>
            <InfoTooltip
              width="w-72"
              content={
                <>
                  <div className="font-semibold mb-1">Discounts</div>
                  <p className="text-gray-300">
                    Create automatic discounts that apply to specific programs during certain periods. Unlike promo codes, discounts are automatically applied without requiring a code. They can have priority settings and work with the discount stacking feature.
                  </p>
                </>
              }
            >
              <FaInfoCircle className={`${isPWA ? 'text-orange-500 hover:text-orange-700' : 'text-gray-400 hover:text-gray-600'}`} size={16} />
            </InfoTooltip>
          </div>
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-4'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} ${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Discount Name*
                  <InfoTooltip
                    width="w-56"
                    content="Enter a descriptive name for this discount (e.g., 'Summer Special', 'Early Bird Discount')."
                  >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="text"
                  value={editingDiscount ? editingDiscount.name : newDiscount.name}
                  onChange={(e) => {
                    if (editingDiscount) {
                      setEditingDiscount({ ...editingDiscount, name: e.target.value });
                    } else {
                      setNewDiscount({ ...newDiscount, name: e.target.value });
                    }
                  }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                placeholder="Summer Special"
                required
              />
            </div>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Discount (%)*
                <InfoTooltip
                  width="w-56"
                  content="Enter the discount percentage (1-100). This will be automatically applied to eligible students."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="number"
                  value={editingDiscount ? editingDiscount.discount : newDiscount.discount}
                  onChange={(e) => {
                    if (editingDiscount) {
                      setEditingDiscount({ ...editingDiscount, discount: e.target.value });
                    } else {
                      setNewDiscount({ ...newDiscount, discount: e.target.value });
                    }
                  }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                placeholder="15"
                min="1"
                max="100"
                required
              />
            </div>
          </div>
          {/* Program Selection for Discounts */}
          <div className={`${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-2'} flex items-center gap-1`}>
              Select Program*
              <InfoTooltip
                width="w-64"
                content="Select which program(s) this discount applies to. The validity period will be based on the program's start date."
              >
                <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
              </InfoTooltip>
            </label>
            <select
              value={selectedProgram}
              onChange={(e) => handleProgramSelection(e.target.value)}
              className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
              required
            >
              <option value="">Select a program</option>
              {memoizedPrograms.filter(p => p.isActive).map(program => (
                <option key={program._id} value={program._id}>
                  {program.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Selection for Discounts */}
          {selectedProgram && (
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} ${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Valid From*
                <InfoTooltip
                  width="w-56"
                  content="The date when this discount becomes active. Must be on or after the program's start date."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
                {isLoadingProgramDetails ? (
                  <div className={`w-full ${isPWA ? 'border-2 border-orange-200' : 'border'} rounded-lg px-3 py-2 ${isPWA ? 'bg-orange-50/50' : 'bg-gray-100'}`}>
                    <div className={`animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
                    <span className={`ml-2 ${isPWA ? 'text-xs sm:text-sm text-orange-700' : 'text-sm text-gray-500'}`}>Loading program details...</span>
                  </div>
                ) : (
              <input
                type="date"
                value={editingDiscount ? editingDiscount.validFrom : newDiscount.validFrom}
                onChange={(e) => {
                  if (editingDiscount) {
                    setEditingDiscount({ ...editingDiscount, validFrom: e.target.value });
                  } else {
                    setNewDiscount({ ...newDiscount, validFrom: e.target.value });
                  }
                }}
                    min={getMinDate()}
                    className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all date-input`}
                required
              />
                )}
                {selectedProgramDetails && (
                  <p className={`${isPWA ? 'text-xs text-orange-700 font-medium' : 'text-xs text-gray-500'} mt-1.5`}>
                    Program starts: {formatDate(selectedProgramDetails.effectiveDate)}
                  </p>
                )}
            </div>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Valid Until*
                <InfoTooltip
                  width="w-56"
                  content="The date when this discount expires. Must be after the 'Valid From' date."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="date"
                value={editingDiscount ? editingDiscount.validUntil : newDiscount.validUntil}
                onChange={(e) => {
                  if (editingDiscount) {
                    setEditingDiscount({ ...editingDiscount, validUntil: e.target.value });
                  } else {
                    setNewDiscount({ ...newDiscount, validUntil: e.target.value });
                  }
                }}
                  min={(editingDiscount ? editingDiscount.validFrom : newDiscount.validFrom) || getMinDate()}
                  className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all date-input`}
                required
                  disabled={!(editingDiscount ? editingDiscount.validFrom : newDiscount.validFrom)}
              />
                {(editingDiscount ? editingDiscount.validFrom : newDiscount.validFrom) && (editingDiscount ? editingDiscount.validUntil : newDiscount.validUntil) && validateDateRange(editingDiscount ? editingDiscount.validFrom : newDiscount.validFrom, editingDiscount ? editingDiscount.validUntil : newDiscount.validUntil) && (
                  <p className={`${isPWA ? 'text-red-600 font-medium' : 'text-red-500'} text-xs mt-1.5`}>{validateDateRange(editingDiscount ? editingDiscount.validFrom : newDiscount.validFrom, editingDiscount ? editingDiscount.validUntil : newDiscount.validUntil)}</p>
                )}
            </div>
          </div>
          )}
          <div className={`${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="discountHasPriority"
                  checked={editingDiscount ? editingDiscount.hasPriority : newDiscount.hasPriority}
                  onChange={(e) => {
                    if (editingDiscount) {
                      setEditingDiscount({ ...editingDiscount, hasPriority: e.target.checked });
                    } else {
                      setNewDiscount({ ...newDiscount, hasPriority: e.target.checked });
                    }
                  }}
                  className={`h-4 w-4 ${isPWA ? 'text-orange-600 focus:ring-orange-500 border-orange-300' : 'text-orange-600 focus:ring-orange-500 border-gray-300'} rounded`}
                />
                <label htmlFor="discountHasPriority" className={`ml-2 block ${isPWA ? 'text-xs sm:text-sm text-orange-800 font-medium' : 'text-sm text-gray-900'} flex items-center gap-1`}>
                  Has priority over other discounts
                  <InfoTooltip
                    width="w-64"
                    content="When enabled, this discount will take precedence over other discounts when multiple discounts are available. Useful for special promotions."
                  >
                    <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                  </InfoTooltip>
                </label>
              </div>
                  </div>
          <div className={`flex ${isPWA ? 'flex-col sm:flex-row gap-3' : 'space-x-2'} relative z-10`}>
          <button
              onClick={editingDiscount ? updateDiscount : addDiscount}
            disabled={
                !selectedProgram ||
                !(editingDiscount ? editingDiscount.name : newDiscount.name) ||
                !(editingDiscount ? editingDiscount.discount : newDiscount.discount) ||
                !(editingDiscount ? editingDiscount.validFrom : newDiscount.validFrom) ||
                !(editingDiscount ? editingDiscount.validUntil : newDiscount.validUntil) ||
              isLoading
            }
            className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg border border-orange-400/50 font-bold' : 'bg-orange-600 text-white px-4 py-2 hover:bg-orange-700 shadow-sm'} rounded-lg transition-all duration-200 ${
                !selectedProgram ||
                !(editingDiscount ? editingDiscount.name : newDiscount.name) ||
                !(editingDiscount ? editingDiscount.discount : newDiscount.discount) ||
                !(editingDiscount ? editingDiscount.validFrom : newDiscount.validFrom) ||
                !(editingDiscount ? editingDiscount.validUntil : newDiscount.validUntil) ||
              isLoading
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
              {editingDiscount ? <FaCheck className="inline mr-1.5" /> : <FaPlus className="inline mr-1.5" />}
              {isLoading ? (editingDiscount ? 'Updating...' : 'Creating...') : (editingDiscount ? 'Update Discount' : 'Create Discount')}
          </button>
            {editingDiscount && (
              <button
                onClick={cancelEditDiscount}
                disabled={isLoading}
                className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white shadow-md hover:shadow-lg border border-gray-300/50 font-bold' : 'bg-gray-500 text-white px-4 py-2 hover:bg-gray-600'} rounded-lg transition-all duration-200`}
              >
                <FaTimes className="inline mr-1.5" />
                Cancel
              </button>
            )}
          </div>
        </div>

          {isLoading ? (
          <div className={`text-center py-8 ${isPWA ? 'bg-gradient-to-br from-orange-50/30 via-amber-50/20 to-yellow-50/10 rounded-xl border border-orange-200/50' : ''}`}>
              <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
              <p className={`mt-2 ${isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}`}>Loading discounts...</p>
            </div>
        ) : discounts.length > 0 ? (
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'}`}>
            {discounts.map(discount => (
              <div key={discount._id} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl hover:shadow-2xl hover:border-orange-300/80' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'} rounded-xl transition-all duration-300 ${isPWA ? 'p-4 sm:p-5' : 'p-5'} relative overflow-hidden group`}>
                {isPWA && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`flex items-start justify-between ${isPWA ? 'mb-3' : 'mb-3'} relative z-10`}>
                  <div className="flex-1">
                    <div className={`flex items-center ${isPWA ? 'gap-2 flex-wrap' : 'gap-2'} ${isPWA ? 'mb-2' : 'mb-2'}`}>
                      <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-900'}`}>{discount.name}</h3>
                      <span
                        className={`inline-block ${isPWA ? 'px-2 py-1 text-[10px] sm:text-xs' : 'px-2.5 py-1 text-xs'} font-bold rounded-full cursor-help shadow-sm ${
                          discount.hasPriority 
                            ? isPWA ? 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-200/50' : 'bg-orange-100 text-orange-800'
                            : isPWA ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border border-gray-200/50' : 'bg-gray-100 text-gray-800'
                        }`}
                        title={discount.hasPriority ? 'This discount has priority and will be applied over other discounts' : 'This discount has normal priority'}
                      >
                        {discount.hasPriority ? 'High Priority' : 'Normal'}
                      </span>
                    </div>
                    <div className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-600' : 'text-green-600'} ${isPWA ? 'mb-3' : 'mb-3'}`} title="Discount percentage automatically applied to eligible students">{discount.discount}%</div>
                  </div>
                </div>
                <div className={`space-y-3 ${isPWA ? 'mb-4' : 'mb-4'} relative z-10`}>
                  <div>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} ${isPWA ? 'text-orange-700 font-bold mb-1.5' : 'text-gray-500 mb-1'} flex items-center gap-1`} title="Programs this discount applies to">
                      Programs
                      <FaInfoCircle className={`${isPWA ? 'text-orange-500' : 'text-gray-400'} cursor-help`} size={10} />
                    </p>
                        <div className="flex flex-wrap gap-1">
                          {discount.programIds.length > 0 ? (
                            discount.programIds.map(programId => (
                              <span
                                key={programId}
                                className={`${isPWA ? 'text-[10px] sm:text-xs px-2 py-1 font-bold shadow-sm' : 'text-xs px-2 py-1'} rounded ${
                                  programNameCache.has(programId) 
                                    ? isPWA ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200/50' : 'bg-orange-100 text-orange-800'
                                    : isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50' : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {getProgramName(programId)}
                              </span>
                            ))
                          ) : (
                            <span className={`${isPWA ? 'text-red-600 font-bold' : 'text-red-600'} ${isPWA ? 'text-xs sm:text-sm' : 'text-xs'}`}>No valid programs</span>
                          )}
                        </div>
                  </div>
                  <div>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} ${isPWA ? 'text-orange-700 font-bold mb-1.5' : 'text-gray-500 mb-1'} flex items-center gap-1`} title="Date range when this discount is valid">
                      Validity Period
                      <FaInfoCircle className={`${isPWA ? 'text-orange-500' : 'text-gray-400'} cursor-help`} size={10} />
                    </p>
                    <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-800 font-semibold' : 'text-gray-700'}`}>{formatDate(discount.validFrom)} - {formatDate(discount.validUntil)}</p>
                  </div>
                  <div>
                    <span
                      className={`inline-block ${isPWA ? 'px-2.5 py-1 text-[10px] sm:text-xs font-bold shadow-sm' : 'px-2.5 py-1 text-xs font-medium'} rounded-full cursor-help ${
                        isExpired(discount.validUntil)
                          ? isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50' : 'bg-red-100 text-red-800'
                          : isUpcoming(discount.validFrom)
                          ? isPWA ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border border-yellow-200/50' : 'bg-yellow-100 text-yellow-800'
                          : isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50' : 'bg-green-100 text-green-800'
                      }`}
                      title={isExpired(discount.validUntil) ? 'This discount has expired' : isUpcoming(discount.validFrom) ? 'This discount is not yet active' : 'This discount is currently active'}
                    >
                      {isExpired(discount.validUntil)
                        ? 'Expired'
                        : isUpcoming(discount.validFrom)
                        ? 'Upcoming'
                        : 'Active'}
                    </span>
                  </div>
                </div>
                <div className={`flex items-center justify-end ${isPWA ? 'gap-2' : 'space-x-2'} pt-3 ${isPWA ? 'border-t border-orange-200/50' : 'border-t border-gray-100'} relative z-10`}>
                        <button
                          onClick={() => startEditDiscount(discount)}
                          disabled={isLoading}
                    className={`${isPWA ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50/80 border border-orange-200/50 shadow-sm' : 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Edit"
                        >
                    <FaEdit size={isPWA ? 14 : 16} />
                        </button>
                        <button
                          onClick={() => showDeleteConfirmation(discount, 'discount')}
                          disabled={isLoading}
                    className={`${isPWA ? 'text-red-600 hover:text-red-800 hover:bg-red-50/80 border border-red-200/50 shadow-sm' : 'text-red-600 hover:text-red-800 hover:bg-red-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Delete"
                        >
                    <FaTrash size={isPWA ? 14 : 16} />
                        </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`text-center py-12 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 rounded-xl' : 'bg-gray-50 rounded-lg border border-gray-200'}`}>
            <p className={isPWA ? 'text-orange-700 font-medium' : 'text-gray-500'}>No discounts found</p>
          </div>
        )}
      </div>
    </div>
  );

  const InstallmentTabPanel = (
    <div>
      <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4'}`}>
        {/* Create/Edit Installment Plan Form */}
        <div className={`mb-6 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-4 sm:p-6 rounded-2xl' : 'p-4 rounded-lg'} relative overflow-visible group`}>
          {isPWA && (
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100/30 to-amber-100/20 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
            </div>
          )}
          <div className={`flex items-center gap-2 ${isPWA ? 'mb-4' : 'mb-3'} relative z-10`}>
            {isPWA && (
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md">
                <FaCreditCard className="text-white text-base" />
              </div>
            )}
            <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} ${isPWA ? 'font-bold text-orange-800' : 'font-medium text-gray-900'}`}>
              {editingInstallment ? 'Edit Installment Plan' : 'Create New Installment Plan'}
            </h3>
            <InfoTooltip
              width="w-72"
              content={
                <>
                  <div className="font-semibold mb-1">Installment Plans</div>
                  <p className="text-gray-300">
                    Create payment plans that allow students to pay program fees in multiple installments. Installment dates are automatically calculated based on the program's start date. Each installment can have a different percentage of the total price.
                  </p>
                </>
              }
            >
              <FaInfoCircle className={`${isPWA ? 'text-orange-500 hover:text-orange-700' : 'text-gray-400 hover:text-gray-600'}`} size={16} />
            </InfoTooltip>
          </div>
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 md:grid-cols-3'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} ${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Plan Name*
                <InfoTooltip
                  width="w-56"
                  content="Enter a descriptive name for this installment plan (e.g., '3-Month Plan', 'Quarterly Payment')."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="text"
                  value={editingInstallment ? editingInstallment.name : newInstallment.name}
                  onChange={(e) => {
                    if (editingInstallment) {
                      setEditingInstallment({ ...editingInstallment, name: e.target.value });
                    } else {
                      setNewInstallment({ ...newInstallment, name: e.target.value });
                    }
                  }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                placeholder="3-Month Plan"
                required
              />
            </div>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Program*
                <InfoTooltip
                  width="w-64"
                  content="Select the program this installment plan applies to. Installment dates will be calculated based on the program's start date."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <select
                  value={editingInstallment ? editingInstallment.programId : newInstallment.programId}
                  onChange={(e) => {
                    if (editingInstallment) {
                      setEditingInstallment({ ...editingInstallment, programId: e.target.value });
                    } else {
                      setNewInstallment({ ...newInstallment, programId: e.target.value });
                    }
                  }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                required
              >
                <option value="">Select Program</option>
                {memoizedPrograms.filter(p => p.isActive).map(program => (
                  <option key={program._id} value={program._id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Number of Installments*
                <InfoTooltip
                  width="w-64"
                  content="Enter the total number of payment installments (1-12). Percentages will be evenly distributed by default (up to 100%). You can adjust them later - if total is less than 100%, the remaining percentage will be applied as a discount."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="number"
                  value={editingInstallment ? editingInstallment.numberOfInstallments : newInstallment.numberOfInstallments}
                onChange={(e) => {
                  const num = Number(e.target.value);
                  if (num < 1 || num > 12) return;
                  
                    const currentIntervals = editingInstallment ? editingInstallment.intervals : newInstallment.intervals;
                  // Calculate dates relative to program's effectiveDate if available
                  const baseDate = selectedProgramDetails?.effectiveDate 
                    ? new Date(selectedProgramDetails.effectiveDate) 
                    : new Date();
                  
                  // Calculate equal percentage distribution (defaults to 100%, user can adjust)
                  const basePercentage = Math.floor(100 / num);
                  const remainder = 100 % num;
                  
                  const newIntervals = Array(num)
                    .fill()
                    .map((_, i) => {
                      // Distribute remainder to first few installments
                      const percentage = i < remainder ? basePercentage + 1 : basePercentage;
                      return currentIntervals[i] ? {
                        ...currentIntervals[i],
                        percentage: percentage,
                      } : {
                        dueDate: new Date(baseDate.getTime() + (i + 1) * 30 * 24 * 60 * 60 * 1000).toISOString(),
                        percentage: percentage,
                      };
                    });
                    if (editingInstallment) {
                      setEditingInstallment({
                        ...editingInstallment,
                        numberOfInstallments: num,
                        intervals: newIntervals,
                      });
                    } else {
                  setNewInstallment({
                    ...newInstallment,
                    numberOfInstallments: num,
                    intervals: newIntervals,
                  });
                    }
                }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                min="1"
                max="12"
                required
              />
            </div>
          </div>

          <div className={`${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-2'} flex items-center gap-1`}>
              Installment Schedule*
              <InfoTooltip
                width="w-72"
                content="Define the payment schedule for this installment plan. Set the number of days after program start and the percentage for each installment. Total percentage can be up to 100%. If less than 100%, the remaining percentage will be applied as a discount. Dates automatically update when the program's start date changes."
              >
                <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
              </InfoTooltip>
            </label>
            <div className={`${isPWA ? 'space-y-3 sm:space-y-4' : 'space-y-3'}`}>
              {(editingInstallment ? editingInstallment.intervals : newInstallment.intervals).map((interval, index) => (
                <div key={index} className={`${isPWA ? 'bg-white/60 border-2 border-orange-200/50 rounded-xl p-3 sm:p-4' : ''} grid ${isPWA ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 md:grid-cols-3'} ${isPWA ? 'gap-3' : 'gap-4'} items-center`}>
                  <div>
                    <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                      Days After Enrollment
                      <InfoTooltip
                        width="w-64"
                        content="Number of days after the program start date when this installment is due. Dates are automatically calculated based on the program's effective date."
                      >
                        <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                      </InfoTooltip>
                    </label>
                    <input
                      type="number"
                      value={(() => {
                        // Calculate days relative to program's effectiveDate if available
                        const baseDate = selectedProgramDetails?.effectiveDate 
                          ? new Date(selectedProgramDetails.effectiveDate) 
                          : new Date();
                        return Math.round((new Date(interval.dueDate) - baseDate) / (24 * 60 * 60 * 1000));
                      })()}
                      onChange={(e) => {
                        // Calculate date relative to program's effectiveDate if available
                        const baseDate = selectedProgramDetails?.effectiveDate 
                          ? new Date(selectedProgramDetails.effectiveDate) 
                          : new Date();
                        
                        if (editingInstallment) {
                          const newIntervals = [...editingInstallment.intervals];
                          newIntervals[index] = {
                            ...newIntervals[index],
                            dueDate: new Date(baseDate.getTime() + Number(e.target.value) * 24 * 60 * 60 * 1000).toISOString(),
                          };
                          setEditingInstallment({
                            ...editingInstallment,
                            intervals: newIntervals,
                          });
                        } else {
                          updateInstallmentInterval(index, 'dueDate', e.target.value);
                        }
                      }}
                      className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white shadow-sm' : 'bg-white'} transition-all`}
                      min="1"
                      required
                    />
                  </div>
                  <div>
                    <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                      Percentage (%)
                      <InfoTooltip
                        width="w-64"
                        content="The percentage of the total program price for this installment. Total of all percentages should not exceed 100%. If total is less than 100%, the remaining percentage will be applied as a discount."
                      >
                        <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                      </InfoTooltip>
                    </label>
                    <input
                      type="number"
                      value={interval.percentage}
                      onChange={(e) => {
                        if (editingInstallment) {
                          const newIntervals = [...editingInstallment.intervals];
                          newIntervals[index] = {
                            ...newIntervals[index],
                            percentage: Number(e.target.value),
                          };
                          setEditingInstallment({
                            ...editingInstallment,
                            intervals: newIntervals,
                          });
                        } else {
                          updateInstallmentInterval(index, 'percentage', e.target.value);
                        }
                      }}
                      className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white shadow-sm' : 'bg-white'} transition-all`}
                      min="1"
                      max="100"
                      required
                    />
                  </div>
                  <div>
                    {(editingInstallment ? editingInstallment.intervals : newInstallment.intervals).length > 1 && (
                      <button
                        onClick={() => {
                          if (editingInstallment) {
                            const newIntervals = [...editingInstallment.intervals];
                            newIntervals.splice(index, 1);
                            const newNum = newIntervals.length;
                            
                            // Recalculate percentages evenly for remaining installments
                            if (newNum > 0) {
                              const basePercentage = Math.floor(100 / newNum);
                              const remainder = 100 % newNum;
                              
                              const updatedIntervals = newIntervals.map((interval, i) => ({
                                ...interval,
                                percentage: i < remainder ? basePercentage + 1 : basePercentage,
                              }));
                              
                              setEditingInstallment({
                                ...editingInstallment,
                                intervals: updatedIntervals,
                                numberOfInstallments: newNum,
                              });
                            } else {
                              setEditingInstallment({
                                ...editingInstallment,
                                intervals: [],
                                numberOfInstallments: 0,
                              });
                            }
                          } else {
                            removeInstallmentInterval(index);
                          }
                        }}
                        className={`${isPWA ? 'text-red-600 hover:text-red-800 hover:bg-red-50/80 border border-red-200/50 px-3 py-2 rounded-lg shadow-sm font-bold text-xs sm:text-sm' : 'text-red-600 hover:text-red-800 text-sm'} transition-all duration-200`}
                      >
                        <FaTrash className="inline mr-1" />
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Total Percentage and Discount Summary */}
              {((editingInstallment ? editingInstallment.intervals : newInstallment.intervals).length > 0) && (() => {
                const intervals = editingInstallment ? editingInstallment.intervals : newInstallment.intervals;
                const totalPercentage = intervals.reduce((sum, interval) => sum + Number(interval.percentage || 0), 0);
                const discount = 100 - totalPercentage;
                const isValid = totalPercentage <= 100;
                
                return (
                  <div className={`${isPWA ? 'mt-4 p-4 rounded-xl border-2' : 'mt-4 p-4 rounded-lg border'} ${
                    !isValid 
                      ? isPWA ? 'bg-red-50/80 border-red-300/50' : 'bg-red-50 border-red-200'
                      : discount > 0
                      ? isPWA ? 'bg-green-50/80 border-green-300/50' : 'bg-green-50 border-green-200'
                      : isPWA ? 'bg-blue-50/80 border-blue-300/50' : 'bg-blue-50 border-blue-200'
                  } relative z-10`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${
                          !isValid ? 'text-red-700' : 'text-gray-700'
                        }`}>
                          Total Installment Percentage:
                        </span>
                        <span className={`${isPWA ? 'text-sm sm:text-base' : 'text-base'} font-bold ${
                          !isValid ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          {totalPercentage.toFixed(2)}%
                        </span>
                      </div>
                      {discount > 0 && isValid && (
                        <div className="flex items-center gap-2">
                          <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium text-green-700`}>
                            Discount Offered:
                          </span>
                          <span className={`${isPWA ? 'text-sm sm:text-base' : 'text-base'} font-bold text-green-600`}>
                            {discount.toFixed(2)}%
                          </span>
                        </div>
                      )}
                      {!isValid && (
                        <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium text-red-600`}>
                          ⚠️ Total cannot exceed 100%
                        </span>
                      )}
                    </div>
                    {discount > 0 && isValid && (
                      <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} text-green-600 mt-2`}>
                        The remaining {discount.toFixed(2)}% will be applied as a discount to the total program price.
                      </p>
                    )}
                  </div>
                );
              })()}
              
              <button 
                onClick={() => {
                  if (editingInstallment) {
                    const newIntervals = [...editingInstallment.intervals];
                    const newNum = newIntervals.length + 1;
                    
                    // Recalculate percentages evenly for all installments
                    const basePercentage = Math.floor(100 / newNum);
                    const remainder = 100 % newNum;
                    
                    const updatedIntervals = newIntervals.map((interval, i) => ({
                      ...interval,
                      percentage: i < remainder ? basePercentage + 1 : basePercentage,
                    }));
                    
                    const baseDate = selectedProgramDetails?.effectiveDate 
                      ? new Date(selectedProgramDetails.effectiveDate) 
                      : new Date();
                    
                    updatedIntervals.push({
                      dueDate: new Date(baseDate.getTime() + newNum * 30 * 24 * 60 * 60 * 1000).toISOString(),
                      percentage: (newIntervals.length < remainder) ? basePercentage + 1 : basePercentage,
                    });
                    
                    setEditingInstallment({
                      ...editingInstallment,
                      intervals: updatedIntervals,
                      numberOfInstallments: newNum,
                    });
                  } else {
                    addInstallmentInterval();
                  }
                }} 
                className={`${isPWA ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50/80 border border-orange-200/50 px-4 py-2 rounded-lg shadow-sm font-bold text-xs sm:text-sm' : 'text-orange-600 hover:text-orange-800 text-sm'} transition-all duration-200`}
              >
                <FaPlus className="inline mr-1.5" />
                Add Installment
              </button>
            </div>
          </div>



          <div className={`flex ${isPWA ? 'flex-col sm:flex-row gap-3' : 'space-x-2'} relative z-10`}>
          <button
              onClick={editingInstallment ? updateInstallmentPlan : addInstallmentPlan}
              disabled={!(editingInstallment ? editingInstallment.name : newInstallment.name) || !(editingInstallment ? editingInstallment.programId : newInstallment.programId) || (editingInstallment ? editingInstallment.intervals : newInstallment.intervals).length === 0 || isLoading}
            className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg border border-orange-400/50 font-bold' : 'bg-orange-600 text-white px-4 py-2 hover:bg-orange-700'} rounded-lg transition-all duration-200 ${
                !(editingInstallment ? editingInstallment.name : newInstallment.name) || !(editingInstallment ? editingInstallment.programId : newInstallment.programId) || (editingInstallment ? editingInstallment.intervals : newInstallment.intervals).length === 0 || isLoading
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
              {editingInstallment ? <FaCheck className="inline mr-1.5" /> : <FaPlus className="inline mr-1.5" />}
              {isLoading ? (editingInstallment ? 'Updating...' : 'Creating...') : (editingInstallment ? 'Update Installment Plan' : 'Create Installment Plan')}
          </button>
            {editingInstallment && (
              <button
                onClick={cancelEditInstallment}
                disabled={isLoading}
                className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white shadow-md hover:shadow-lg border border-gray-300/50 font-bold' : 'bg-gray-500 text-white px-4 py-2 hover:bg-gray-600'} rounded-lg transition-all duration-200`}
              >
                <FaTimes className="inline mr-1.5" />
                Cancel
              </button>
            )}
          </div>
        </div>

          {isLoading ? (
          <div className={`text-center py-8 ${isPWA ? 'bg-gradient-to-br from-orange-50/30 via-amber-50/20 to-yellow-50/10 rounded-xl border border-orange-200/50' : ''}`}>
              <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
              <p className={`mt-2 ${isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}`}>Loading installment plans...</p>
            </div>
        ) : installmentPlans.length > 0 ? (
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'}`}>
            {[...installmentPlans]
              .sort((a, b) => {
                // Calculate discount for each plan
                const discountA = 100 - (a.intervals?.reduce((sum, interval) => sum + Number(interval.percentage || 0), 0) || 0);
                const discountB = 100 - (b.intervals?.reduce((sum, interval) => sum + Number(interval.percentage || 0), 0) || 0);
                // Sort by discount descending (highest discount first)
                return discountB - discountA;
              })
              .map(plan => (
              <div key={plan._id} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl hover:shadow-2xl hover:border-orange-300/80' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'} rounded-xl transition-all duration-300 ${isPWA ? 'p-4 sm:p-5' : 'p-5'} relative overflow-hidden group`}>
                {isPWA && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`flex items-start justify-between ${isPWA ? 'mb-3' : 'mb-3'} relative z-10`}>
                  <div className="flex-1">
                    <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-900'} ${isPWA ? 'mb-2' : 'mb-2'}`}>{plan.name}</h3>
                    <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 font-medium mb-2' : 'text-gray-600 mb-2'}`}>{getProgramName(plan.programId)}</p>
                    <span
                      className={`inline-block ${isPWA ? 'px-2.5 py-1 text-[10px] sm:text-xs' : 'px-2.5 py-1 text-xs'} font-bold rounded-full cursor-help shadow-sm ${
                        plan.isActive 
                          ? isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50' : 'bg-green-100 text-green-800'
                          : isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50' : 'bg-red-100 text-red-800'
                      }`}
                      title={plan.isActive ? 'This installment plan is active and available for students' : 'This installment plan is inactive and not available'}
                    >
                      {plan.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {(() => {
                    const totalPercentage = plan.intervals?.reduce((sum, interval) => sum + Number(interval.percentage || 0), 0) || 0;
                    const discount = 100 - totalPercentage;
                    if (discount > 0) {
                      return (
                        <div className={`${isPWA ? 'ml-3' : 'ml-3'} flex-shrink-0`}>
                          <span
                            className={`inline-block ${isPWA ? 'px-3 py-1.5 text-xs sm:text-sm' : 'px-3 py-1.5 text-sm'} font-bold rounded-lg cursor-help shadow-md ${
                              isPWA 
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white border border-green-400/50' 
                                : 'bg-green-500 text-white'
                            }`}
                            title={`This plan offers ${discount.toFixed(2)}% discount on the total program price`}
                          >
                            {discount.toFixed(0)}% OFF
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className={`mt-4 pt-4 ${isPWA ? 'border-t-2 border-orange-200/50' : 'border-t border-gray-200'} relative z-10`}>
                  <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} ${isPWA ? 'text-orange-700 font-bold mb-2' : 'text-gray-500 mb-2'} flex items-center gap-1`} title="Payment schedule showing each installment's percentage and due date">
                    Installment Schedule
                    <FaInfoCircle className={`${isPWA ? 'text-orange-500' : 'text-gray-400'} cursor-help`} size={10} />
                  </p>
                  <div className={`${isPWA ? 'space-y-2 sm:space-y-3' : 'space-y-2'} ${isPWA ? 'mb-4' : 'mb-4'}`}>
                    {plan.intervals.map((interval, idx) => (
                      <div key={idx} className={`flex items-center justify-between ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'bg-white/80 border border-orange-200/50 p-2.5 sm:p-3 rounded-lg shadow-sm' : 'bg-gray-50 p-2 rounded'}`} title={`Installment ${idx + 1}: ${interval.percentage}% due on ${formatDate(interval.dueDate)}`}>
                        <span className={`font-bold ${isPWA ? 'text-orange-800' : 'font-medium text-gray-700'}`}>Installment {idx + 1}</span>
                        <div className="text-right">
                          <span className={`font-bold ${isPWA ? 'text-orange-600' : 'font-semibold text-gray-900'}`} title={`${interval.percentage}% of total program price`}>{interval.percentage}%</span>
                          <span className={`${isPWA ? 'text-[10px] sm:text-xs text-orange-700 font-medium' : 'text-xs text-gray-500'} block`} title={`Due date: ${formatDate(interval.dueDate)}`}>{formatDate(interval.dueDate)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={`flex items-center justify-end ${isPWA ? 'gap-2' : 'space-x-2'} pt-3 ${isPWA ? 'border-t border-orange-200/50' : 'border-t border-gray-100'} relative z-10`}>
                        <button
                          onClick={() => toggleInstallmentPlanStatus(plan._id, plan.isActive)}
                          disabled={isLoading}
                      className={`${plan.isActive 
                        ? isPWA ? 'text-green-600 hover:text-green-800 hover:bg-green-50/80 border border-green-200/50' : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                        : isPWA ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-50/80 border border-gray-200/50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg shadow-sm' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title={plan.isActive ? 'Plan is Active' : 'Activate Plan'}
                        >
                      {plan.isActive ? <FaToggleOn size={isPWA ? 16 : 18} /> : <FaToggleOff size={isPWA ? 16 : 18} />}
                        </button>
                        <button
                          onClick={() => startEditInstallment(plan)}
                          disabled={isLoading}
                      className={`${isPWA ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50/80 border border-orange-200/50 shadow-sm' : 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Edit"
                        >
                      <FaEdit size={isPWA ? 14 : 16} />
                        </button>
                        <button
                          onClick={() => showDeleteConfirmation(plan, 'installment')}
                          disabled={isLoading}
                      className={`${isPWA ? 'text-red-600 hover:text-red-800 hover:bg-red-50/80 border border-red-200/50 shadow-sm' : 'text-red-600 hover:text-red-800 hover:bg-red-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Delete"
                        >
                      <FaTrash size={isPWA ? 14 : 16} />
                        </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`text-center py-12 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 rounded-xl' : 'bg-gray-50 rounded-lg border border-gray-200'}`}>
            <p className={isPWA ? 'text-orange-700 font-medium' : 'text-gray-500'}>No installment plans found</p>
          </div>
        )}
      </div>
    </div>
  );

  const ScholarshipTabPanel = (
    <div>
      <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4'}`}>
        {/* Create/Edit Scholarship Offer Form */}
        <div className={`mb-6 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-4 sm:p-6 rounded-2xl' : 'p-4 rounded-lg'} relative overflow-visible group`}>
          {isPWA && (
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100/30 to-amber-100/20 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
            </div>
          )}
          <div className={`flex items-center gap-2 ${isPWA ? 'mb-4' : 'mb-3'} relative z-10`}>
            {isPWA && (
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md">
                <FaGraduationCap className="text-white text-base" />
              </div>
            )}
            <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} ${isPWA ? 'font-bold text-orange-800' : 'font-medium text-gray-900'}`}>
              {editingScholarship ? 'Edit Scholarship Offer' : 'Create New Scholarship Offer'}
            </h3>
            <InfoTooltip
              width="w-72"
              content={
                <>
                  <div className="font-semibold mb-1">Scholarship Offers</div>
                  <p className="text-gray-300">
                    Create scholarship offers that students can apply for. Scholarships provide discounts based on merit, need, or other criteria. Students submit applications with reasons, and you can approve or reject them.
                  </p>
                </>
              }
            >
              <FaInfoCircle className={`${isPWA ? 'text-orange-500 hover:text-orange-700' : 'text-gray-400 hover:text-gray-600'}`} size={16} />
            </InfoTooltip>
          </div>
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 md:grid-cols-3'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} ${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Offer Name*
                <InfoTooltip
                  width="w-56"
                  content="Enter a descriptive name for this scholarship (e.g., 'Merit Scholarship', 'Need-Based Aid')."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="text"
                  value={editingScholarship ? editingScholarship.name : newScholarshipOffer.name}
                  onChange={(e) => {
                    if (editingScholarship) {
                      setEditingScholarship({ ...editingScholarship, name: e.target.value });
                    } else {
                      setNewScholarshipOffer({ ...newScholarshipOffer, name: e.target.value });
                    }
                  }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                placeholder="Merit Scholarship"
                required
              />
            </div>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Program*
                <InfoTooltip
                  width="w-64"
                  content="Select the program this scholarship offer applies to. Students can apply for this scholarship when enrolling in this program."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <select
                  value={editingScholarship ? editingScholarship.programId : newScholarshipOffer.programId}
                  onChange={(e) => {
                    if (editingScholarship) {
                      setEditingScholarship({ ...editingScholarship, programId: e.target.value });
                    } else {
                      setNewScholarshipOffer({ ...newScholarshipOffer, programId: e.target.value });
                    }
                  }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                required
              >
                <option value="">Select Program</option>
                {memoizedPrograms.filter(p => p.isActive).map(program => (
                  <option key={program._id} value={program._id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'} flex items-center gap-1`}>
                Discount (%)*
                <InfoTooltip
                  width="w-64"
                  content="Enter the discount percentage (1-100) that will be applied if the scholarship application is approved."
                >
                  <FaInfoCircle className={`${isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-gray-600'}`} size={12} />
                </InfoTooltip>
              </label>
              <input
                type="number"
                  value={editingScholarship ? editingScholarship.discountPercentage : newScholarshipOffer.discountPercentage}
                  onChange={(e) => {
                    if (editingScholarship) {
                      setEditingScholarship({ ...editingScholarship, discountPercentage: e.target.value });
                    } else {
                      setNewScholarshipOffer({ ...newScholarshipOffer, discountPercentage: e.target.value });
                    }
                  }}
                className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                placeholder="20"
                min="1"
                max="100"
                required
              />
            </div>
          </div>
          <div className={`${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
            <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'}`}>Description</label>
            <textarea
              value={editingScholarship ? editingScholarship.description : newScholarshipOffer.description}
              onChange={(e) => {
                if (editingScholarship) {
                  setEditingScholarship({ ...editingScholarship, description: e.target.value });
                } else {
                  setNewScholarshipOffer({ ...newScholarshipOffer, description: e.target.value });
                }
              }}
              className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
              rows="3"
              placeholder="Describe this scholarship offer..."
            />
          </div>

          <div className={`flex ${isPWA ? 'flex-col sm:flex-row gap-3' : 'space-x-2'} relative z-10`}>
          <button
              onClick={editingScholarship ? updateScholarshipOffer : addScholarshipOffer}
            disabled={
                !(editingScholarship ? editingScholarship.name : newScholarshipOffer.name) || 
                !(editingScholarship ? editingScholarship.programId : newScholarshipOffer.programId) || 
                !(editingScholarship ? editingScholarship.discountPercentage : newScholarshipOffer.discountPercentage) || 
                isLoading
            }
            className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg border border-orange-400/50 font-bold' : 'bg-orange-600 text-white px-4 py-2 hover:bg-orange-700 shadow-sm'} rounded-lg transition-all duration-200 ${
                !(editingScholarship ? editingScholarship.name : newScholarshipOffer.name) || 
                !(editingScholarship ? editingScholarship.programId : newScholarshipOffer.programId) || 
                !(editingScholarship ? editingScholarship.discountPercentage : newScholarshipOffer.discountPercentage) || 
                isLoading
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
              {editingScholarship ? <FaCheck className="inline mr-1.5" /> : <FaPlus className="inline mr-1.5" />}
              {isLoading ? (editingScholarship ? 'Updating...' : 'Creating...') : (editingScholarship ? 'Update Scholarship Offer' : 'Create Scholarship Offer')}
          </button>
            {editingScholarship && (
              <button
                onClick={cancelEditScholarship}
                disabled={isLoading}
                className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white shadow-md hover:shadow-lg border border-gray-300/50 font-bold' : 'bg-gray-500 text-white px-4 py-2 hover:bg-gray-600'} rounded-lg transition-all duration-200`}
              >
                <FaTimes className="inline mr-1.5" />
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Scholarship Offers Section */}
        <div className={`${isPWA ? 'mb-6' : 'mb-8'}`}>
          <h3 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-lg'} ${isPWA ? 'font-bold text-orange-800 mb-4' : 'font-medium text-gray-900 mb-4'} flex items-center gap-2`}>
            {isPWA && (
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-1.5 rounded-lg shadow-md">
                <FaGraduationCap className="text-white text-sm" />
              </div>
            )}
            Scholarship Offers
          </h3>
          {isLoading ? (
          <div className={`text-center py-8 mb-8 ${isPWA ? 'bg-gradient-to-br from-orange-50/30 via-amber-50/20 to-yellow-50/10 rounded-xl border border-orange-200/50' : ''}`}>
              <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
              <p className={`mt-2 ${isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}`}>Loading scholarship offers...</p>
            </div>
        ) : scholarshipOffers.length > 0 ? (
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} mb-8`}>
            {scholarshipOffers.map(offer => (
              <div key={offer._id} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl hover:shadow-2xl hover:border-orange-300/80' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'} rounded-xl transition-all duration-300 ${isPWA ? 'p-4 sm:p-5' : 'p-5'} relative overflow-hidden group`}>
                {isPWA && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`flex items-start justify-between ${isPWA ? 'mb-3' : 'mb-3'} relative z-10`}>
                  <div className="flex-1">
                    <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-900'} ${isPWA ? 'mb-2' : 'mb-2'}`}>{offer.name}</h3>
                    <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 font-medium mb-2' : 'text-gray-600 mb-2'}`}>{getProgramName(offer.programId)}</p>
                    <span
                      className={`inline-block ${isPWA ? 'px-2.5 py-1 text-[10px] sm:text-xs' : 'px-2.5 py-1 text-xs'} font-bold rounded-full cursor-help shadow-sm ${
                        offer.isActive 
                          ? isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50' : 'bg-green-100 text-green-800'
                          : isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50' : 'bg-red-100 text-red-800'
                      }`}
                      title={offer.isActive ? 'This scholarship offer is active and students can apply' : 'This scholarship offer is inactive and not available'}
                    >
                      {offer.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className={`mt-4 pt-4 ${isPWA ? 'border-t-2 border-orange-200/50' : 'border-t border-gray-200'} relative z-10`}>
                  <div className={`flex items-baseline justify-between ${isPWA ? 'mb-3' : 'mb-3'}`}>
                    <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 font-bold' : 'text-gray-600'} flex items-center gap-1`} title="Discount percentage if scholarship is approved">
                      Discount
                      <FaInfoCircle className={`${isPWA ? 'text-orange-500' : 'text-gray-400'} cursor-help`} size={10} />
                    </span>
                    <span className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-600' : 'text-purple-600'}`} title={`${offer.discountPercentage}% discount applied if approved`}>{offer.discountPercentage}%</span>
                  </div>
                  {offer.description && (
                    <div className={`${isPWA ? 'mb-3' : 'mb-3'}`}>
                      <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} ${isPWA ? 'text-orange-700 font-bold mb-1.5' : 'text-gray-500 mb-1'} flex items-center gap-1`} title="Description of the scholarship offer">
                        Description
                        <FaInfoCircle className={`${isPWA ? 'text-orange-500' : 'text-gray-400'} cursor-help`} size={10} />
                      </p>
                      <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-800 font-medium' : 'text-gray-700'} line-clamp-2`}>{offer.description}</p>
                    </div>
                  )}
                  <div className={`flex items-center justify-end ${isPWA ? 'gap-2' : 'space-x-2'} pt-3 ${isPWA ? 'border-t border-orange-200/50' : 'border-t border-gray-100'} relative z-10`}>
                        <button
                          onClick={() => toggleScholarshipOfferStatus(offer._id, !offer.isActive)}
                          disabled={isLoading}
                      className={`${offer.isActive 
                        ? isPWA ? 'text-green-600 hover:text-green-800 hover:bg-green-50/80 border border-green-200/50' : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                        : isPWA ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-50/80 border border-gray-200/50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'} ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : ''
                      } ${isPWA ? 'p-2 rounded-lg shadow-sm' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title={offer.isActive ? 'Scholarship is Active' : 'Activate Scholarship'}
                        >
                      {offer.isActive ? <FaToggleOn size={isPWA ? 16 : 18} /> : <FaToggleOff size={isPWA ? 16 : 18} />}
                        </button>
                        <button
                          onClick={() => startEditScholarship(offer)}
                          disabled={isLoading}
                      className={`${isPWA ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50/80 border border-orange-200/50 shadow-sm' : 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Edit"
                        >
                      <FaEdit size={isPWA ? 14 : 16} />
                        </button>
                        <button
                          onClick={() => showDeleteConfirmation(offer, 'scholarship')}
                          disabled={isLoading}
                      className={`${isPWA ? 'text-red-600 hover:text-red-800 hover:bg-red-50/80 border border-red-200/50 shadow-sm' : 'text-red-600 hover:text-red-800 hover:bg-red-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Delete"
                        >
                      <FaTrash size={isPWA ? 14 : 16} />
                        </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`text-center py-12 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 rounded-xl' : 'bg-gray-50 rounded-lg border border-gray-200'} mb-8`}>
            <p className={isPWA ? 'text-orange-700 font-medium' : 'text-gray-500'}>No scholarship offers found</p>
        </div>
        )}
        </div>

        {/* Scholarship Applications Section */}
        <div>
          <h3 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-lg'} ${isPWA ? 'font-bold text-orange-800 mb-4' : 'font-medium text-gray-900 mb-4'} flex items-center gap-2`}>
            {isPWA && (
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-1.5 rounded-lg shadow-md">
                <FaUserGraduate className="text-white text-sm" />
              </div>
            )}
            Scholarship Applications
          </h3>
          {isLoading ? (
          <div className={`text-center py-8 ${isPWA ? 'bg-gradient-to-br from-orange-50/30 via-amber-50/20 to-yellow-50/10 rounded-xl border border-orange-200/50' : ''}`}>
              <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
              <p className={`mt-2 ${isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}`}>Loading scholarship applications...</p>
            </div>
        ) : scholarshipApplications.length > 0 ? (
          <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'}`}>
            {scholarshipApplications.map(application => (
              <div key={application._id} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl hover:shadow-2xl hover:border-orange-300/80' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'} rounded-xl transition-all duration-300 ${isPWA ? 'p-4 sm:p-5' : 'p-5'} relative overflow-hidden group`}>
                {isPWA && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`flex items-start justify-between ${isPWA ? 'mb-3' : 'mb-3'} relative z-10`}>
                  <div className="flex-1">
                    <div className={`flex items-center ${isPWA ? 'gap-2 mb-2' : 'gap-2 mb-2'}`}>
                      <FaUserGraduate className={isPWA ? 'text-orange-500' : 'text-gray-500'} />
                      <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-900'}`}>{getStudentName(application.studentId)}</h3>
                        </div>
                    <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 font-medium mb-1' : 'text-gray-600 mb-1'}`}>
                      <span className="font-bold">Offer:</span> {scholarshipOffers.find(o => o._id === application.scholarshipOfferId)?.name || 'Unknown Offer'}
                    </p>
                    <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 font-medium mb-2' : 'text-gray-600 mb-2'}`}>
                      <span className="font-bold">Program:</span> {getProgramName(application.programId)}
                    </p>
                    <span className={`inline-block ${isPWA ? 'px-2.5 py-1 text-[10px] sm:text-xs' : 'px-2.5 py-1 text-xs'} font-bold rounded-full shadow-sm ${
                          application.status === 'approved' 
                            ? isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50' : 'bg-green-100 text-green-800'
                            : application.status === 'rejected' 
                              ? isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50' : 'bg-red-100 text-red-800'
                              : isPWA ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border border-yellow-200/50' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                        </span>
                  </div>
                </div>
                <div className={`mt-4 pt-4 ${isPWA ? 'border-t-2 border-orange-200/50' : 'border-t border-gray-200'} relative z-10`}>
                  <div className={`${isPWA ? 'mb-3' : 'mb-3'}`}>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} ${isPWA ? 'text-orange-700 font-bold mb-1.5' : 'text-gray-500 mb-1'}`}>Reason</p>
                    <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-800 font-medium' : 'text-gray-700'} line-clamp-3`}>{application.reason}</p>
                  </div>
                  <div className={`${isPWA ? 'mb-3' : 'mb-3'}`}>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} ${isPWA ? 'text-orange-700 font-bold mb-1.5' : 'text-gray-500 mb-1'}`}>Applied On</p>
                    <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-800 font-semibold' : 'text-gray-700'}`}>{formatDate(application.createdAt)}</p>
                  </div>
                  <div className={`flex items-center justify-end ${isPWA ? 'gap-2' : 'space-x-2'} pt-3 ${isPWA ? 'border-t border-orange-200/50' : 'border-t border-gray-100'} relative z-10`}>
                        {application.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateScholarshipApplicationStatus(application._id, 'approved')}
                              disabled={isLoading}
                          className={`${isPWA ? 'text-green-600 hover:text-green-800 hover:bg-green-50/80 border border-green-200/50 shadow-sm' : 'text-green-600 hover:text-green-800 hover:bg-green-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                              title="Approve"
                            >
                          <FaCheck size={isPWA ? 14 : 16} />
                            </button>
                            <button
                              onClick={() => updateScholarshipApplicationStatus(application._id, 'rejected')}
                              disabled={isLoading}
                          className={`${isPWA ? 'text-red-600 hover:text-red-800 hover:bg-red-50/80 border border-red-200/50 shadow-sm' : 'text-red-600 hover:text-red-800 hover:bg-red-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                              title="Reject"
                            >
                          <FaTimes size={isPWA ? 14 : 16} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setShowDeleteScholarshipConfirm(application._id)}
                          disabled={isLoading}
                      className={`${isPWA ? 'text-red-600 hover:text-red-800 hover:bg-red-50/80 border border-red-200/50 shadow-sm' : 'text-red-600 hover:text-red-800 hover:bg-red-50'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isPWA ? 'p-2 rounded-lg' : 'p-2 rounded-lg'} transition-all duration-200`}
                          title="Delete"
                        >
                      <FaTrash size={isPWA ? 14 : 16} />
                        </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`text-center py-12 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 rounded-xl' : 'bg-gray-50 rounded-lg border border-gray-200'}`}>
            <p className={isPWA ? 'text-orange-700 font-medium' : 'text-gray-500'}>No scholarship applications found</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );

  // Fetch referral settings and analytics
  const fetchReferralSettings = useCallback(async () => {
    try {
      setIsLoadingReferralSettings(true);
      const trainerId = session?.trainerId || session?.id;
      if (!trainerId) return;

      // Fetch trainer profile to get referral settings
      const trainerResponse = await HttpInterceptor.get('/api/trainer/profile');
      // Handle different response formats (response.data.data or response.data)
      const profileData = trainerResponse.data?.data || trainerResponse.data || {};
      
      const isAdvancePlan = profileData.subscriptionPlan === 'advance';
      const referralFeatureEnabled = profileData.advanceFeatures?.referralCodeSystem === true;
      
      setHasReferralFeature(isAdvancePlan && referralFeatureEnabled);
      
      if (isAdvancePlan && referralFeatureEnabled) {
        setReferralSettings({
          referralDiscountType: profileData.referralDiscountType || 'percentage',
          referralDiscountPercentage: profileData.referralDiscountPercentage || 0,
          referralDiscountAmount: profileData.referralDiscountAmount || 0,
          referralDiscountForReferredStudent: profileData.referralDiscountForReferredStudent || false,
          referralSystemEnabled: profileData.referralSystemEnabled !== undefined ? profileData.referralSystemEnabled : true,
          referralCodeExpiryDays: profileData.referralCodeExpiryDays || 90
        });

        // Fetch analytics
        try {
          const analyticsResponse = await HttpInterceptor.get('/api/trainer/referral-analytics');
          setReferralAnalytics(analyticsResponse.data?.analytics);
        } catch (err) {
          console.error('Error fetching referral analytics:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching referral settings:', error);
    } finally {
      setIsLoadingReferralSettings(false);
    }
  }, [session]);

  // Save referral settings
  const saveReferralSettings = async () => {
    try {
      setIsLoadingReferralSettings(true);
      await HttpInterceptor.put('/api/trainer-settings/referral', referralSettings);
      toast.success('Referral settings saved successfully');
      await fetchReferralSettings();
    } catch (error) {
      console.error('Error saving referral settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save referral settings');
    } finally {
      setIsLoadingReferralSettings(false);
    }
  };

  // Handle referral system toggle confirmation
  const handleReferralSystemToggle = async () => {
    try {
      setIsLoadingReferralSettings(true);
      const updatedSettings = {
        ...referralSettings,
        referralSystemEnabled: pendingReferralSystemEnabled
      };
      await HttpInterceptor.put('/api/trainer-settings/referral', updatedSettings);
      setReferralSettings(updatedSettings);
      toast.success(
        pendingReferralSystemEnabled 
          ? 'Referral system enabled successfully' 
          : 'Referral system disabled. All existing codes will remain valid and must be honored.'
      );
      setShowReferralSystemConfirm(false);
      setPendingReferralSystemEnabled(null);
      await fetchReferralSettings();
    } catch (error) {
      console.error('Error updating referral system status:', error);
      toast.error(error.response?.data?.message || 'Failed to update referral system status');
    } finally {
      setIsLoadingReferralSettings(false);
    }
  };

  useEffect(() => {
    const referralTabIndex = isConsultant ? 2 : 5;
    if (activeTab === referralTabIndex) {
      fetchReferralSettings();
    }
  }, [activeTab, isConsultant, fetchReferralSettings]);

  const ReferralSettingsTabPanel = (
    <div>
      <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4'}`}>
        {isLoadingReferralSettings ? (
          <div className={`${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-6 sm:p-8 rounded-2xl' : 'p-6 rounded-lg'} text-center`}>
            <FaSpinner className={`${isPWA ? 'text-4xl sm:text-5xl' : 'text-5xl'} ${isPWA ? 'text-orange-500' : 'text-blue-600'} mx-auto mb-4 animate-spin`} />
            <h3 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-xl'} ${isPWA ? 'font-bold text-orange-800' : 'font-medium text-gray-900'} mb-2`}>
              Loading Referral Settings...
            </h3>
            <p className={`${isPWA ? 'text-sm sm:text-base text-orange-700' : 'text-gray-600'}`}>
              Please wait while we fetch your referral code settings.
            </p>
          </div>
        ) : !hasReferralFeature ? (
          <div className={`${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-6 sm:p-8 rounded-2xl' : 'p-6 rounded-lg'} text-center`}>
            <FaUserFriends className={`${isPWA ? 'text-4xl sm:text-5xl' : 'text-5xl'} ${isPWA ? 'text-orange-500' : 'text-gray-400'} mx-auto mb-4`} />
            <h3 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-xl'} ${isPWA ? 'font-bold text-orange-800' : 'font-medium text-gray-900'} mb-2`}>
              Referral Code Feature
            </h3>
            <p className={`${isPWA ? 'text-sm sm:text-base text-orange-700' : 'text-gray-600'} mb-4`}>
              This feature is only available for trainers with <strong>Advance</strong> subscription and the referral code feature enabled by admin.
            </p>
            <p className={`${isPWA ? 'text-xs sm:text-sm text-orange-600' : 'text-sm text-gray-500'}`}>
              {session?.subscriptionPlan !== 'advance' 
                ? 'Upgrade to Advance subscription to enable referral codes and reward your students for bringing in new members.'
                : 'Please contact admin to enable the referral code feature for your account.'}
            </p>
          </div>
        ) : (
          <>
            {/* Referral Settings Form */}
            <div className={`mb-6 ${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-4 sm:p-6 rounded-2xl' : 'p-4 rounded-lg'} relative overflow-visible group`}>
              {isPWA && (
                <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
                </div>
              )}
              <div className={`flex items-center justify-between ${isPWA ? 'mb-4' : 'mb-3'} relative z-10`}>
                <div className="flex items-center gap-2">
                  {isPWA && (
                    <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md">
                      <FaUserFriends className="text-white text-base" />
                    </div>
                  )}
                  <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} ${isPWA ? 'font-bold text-orange-800' : 'font-medium text-gray-900'}`}>
                    Referral Code Settings
                  </h3>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700' : 'text-gray-600'} font-medium whitespace-nowrap`}>
                    {referralSettings.referralSystemEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setPendingReferralSystemEnabled(!referralSettings.referralSystemEnabled);
                      setShowReferralSystemConfirm(true);
                    }}
                    disabled={isLoadingReferralSettings}
                    className={`relative inline-flex items-center ${isPWA ? 'h-7 w-14' : 'h-6 w-11'} flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                      referralSettings.referralSystemEnabled 
                        ? (isPWA ? 'bg-orange-500' : 'bg-orange-600') 
                        : 'bg-gray-200'
                    } ${
                      isLoadingReferralSettings 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'cursor-pointer'
                    }`}
                    role="switch"
                    aria-checked={referralSettings.referralSystemEnabled}
                    aria-disabled={isLoadingReferralSettings}
                  >
                    <span
                      className={`pointer-events-none inline-block ${isPWA ? 'h-6 w-6' : 'h-5 w-5'} transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        referralSettings.referralSystemEnabled 
                          ? (isPWA ? 'translate-x-7' : 'translate-x-5') 
                          : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} ${isPWA ? 'mb-5' : 'mb-4'} relative z-10`}>
                <div>
                  <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'}`}>
                    Discount Type
                  </label>
                  <select
                    value={referralSettings.referralDiscountType}
                    onChange={(e) => setReferralSettings({
                      ...referralSettings,
                      referralDiscountType: e.target.value
                    })}
                    className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                  <p className={`${isPWA ? 'text-xs text-orange-600 mt-1' : 'text-xs text-gray-500 mt-1'}`}>
                    Choose how the discount is applied
                  </p>
                </div>
                {referralSettings.referralDiscountType === 'percentage' ? (
                  <div>
                    <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'}`}>
                      Discount Percentage (%)
                    </label>
                    <input
                      type="number"
                      value={referralSettings.referralDiscountPercentage}
                      onChange={(e) => setReferralSettings({
                        ...referralSettings,
                        referralDiscountPercentage: parseFloat(e.target.value) || 0
                      })}
                      className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                      placeholder="10"
                      min="0"
                      max="100"
                    />
                    <p className={`${isPWA ? 'text-xs text-orange-600 mt-1' : 'text-xs text-gray-500 mt-1'}`}>
                      Percentage discount applied when referral code is used
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'}`}>
                      Discount Amount (₹)
                    </label>
                    <input
                      type="number"
                      value={referralSettings.referralDiscountAmount}
                      onChange={(e) => setReferralSettings({
                        ...referralSettings,
                        referralDiscountAmount: parseFloat(e.target.value) || 0
                      })}
                      className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                      placeholder="100"
                      min="0"
                      step="0.01"
                    />
                    <p className={`${isPWA ? 'text-xs text-orange-600 mt-1' : 'text-xs text-gray-500 mt-1'}`}>
                      Fixed amount discount applied when referral code is used
                    </p>
                  </div>
                )}
                <div>
                  <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800 mb-2' : 'text-gray-700 mb-1'}`}>
                    Code Expiry Days
                  </label>
                  <input
                    type="number"
                    value={referralSettings.referralCodeExpiryDays}
                    onChange={(e) => setReferralSettings({
                      ...referralSettings,
                      referralCodeExpiryDays: parseInt(e.target.value) || 90
                    })}
                    className={`w-full ${isPWA ? 'px-3 py-2.5 text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80 shadow-sm' : 'bg-white'} transition-all`}
                    placeholder="90"
                    min="1"
                  />
                  <p className={`${isPWA ? 'text-xs text-orange-600 mt-1' : 'text-xs text-gray-500 mt-1'}`}>
                    How long codes remain valid
                  </p>
                </div>
                <div className={`${isPWA ? 'col-span-1 sm:col-span-2' : 'md:col-span-2'}`}>
                  <div className={`${isPWA ? 'bg-white/80 border-2 border-orange-200/50' : 'bg-white border border-gray-300'} ${isPWA ? 'p-3 sm:p-4 rounded-xl' : 'p-4 rounded-lg'}`}>
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={referralSettings.referralDiscountForReferredStudent}
                        onChange={(e) => setReferralSettings({
                          ...referralSettings,
                          referralDiscountForReferredStudent: e.target.checked
                        })}
                        className={`mt-1 ${isPWA ? 'w-5 h-5' : 'w-4 h-4'} text-orange-600 border-gray-300 rounded focus:ring-orange-500 focus:ring-2`}
                      />
                      <div className="ml-3 flex-1">
                        <p className={`${isPWA ? 'text-sm sm:text-base font-semibold' : 'text-sm font-medium'} ${isPWA ? 'text-orange-800' : 'text-gray-900'}`}>
                          Apply discount to referred students
                        </p>
                        <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-xs'} ${isPWA ? 'text-orange-600 mt-1' : 'text-gray-500 mt-1'}`}>
                          When enabled, students who enroll using a referral code will also receive a discount on their first payment. This incentivizes both the code owner and the referred student.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className={`flex ${isPWA ? 'flex-col sm:flex-row gap-3' : 'space-x-2'} relative z-10`}>
                <button
                  onClick={saveReferralSettings}
                  disabled={isLoadingReferralSettings}
                  className={`${isPWA ? 'w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg border border-orange-400/50 font-bold' : 'bg-orange-600 text-white px-4 py-2 hover:bg-orange-700 shadow-sm'} rounded-lg transition-all duration-200 ${
                    isLoadingReferralSettings ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoadingReferralSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>

            {/* Statistics Dashboard */}
            {referralAnalytics && (
              <div className={`${isPWA ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-white border border-gray-200'} ${isPWA ? 'p-4 sm:p-6 rounded-2xl' : 'p-6 rounded-lg'}`}>
                <h3 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-lg'} ${isPWA ? 'font-bold text-orange-800 mb-4' : 'font-medium text-gray-900 mb-4'} flex items-center gap-2`}>
                  {isPWA && (
                    <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-1.5 rounded-lg shadow-md">
                      <FaChartBar className="text-white text-sm" />
                    </div>
                  )}
                  Referral Statistics
                </h3>
                
                {/* Overall Statistics */}
                <div className={`grid ${isPWA ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} mb-6`}>
                  <div className={`${isPWA ? 'bg-white/80 border-2 border-orange-200/50' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-3 sm:p-4 rounded-xl' : 'p-4 rounded-lg'}`}>
                    <p className={`${isPWA ? 'text-xs sm:text-sm text-orange-600' : 'text-sm text-gray-600'} mb-1`}>Total Codes</p>
                    <p className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-orange-800' : 'text-2xl font-bold text-gray-900'}`}>
                      {referralAnalytics.totalCodes || 0}
                    </p>
                  </div>
                  <div className={`${isPWA ? 'bg-white/80 border-2 border-orange-200/50' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-3 sm:p-4 rounded-xl' : 'p-4 rounded-lg'}`}>
                    <p className={`${isPWA ? 'text-xs sm:text-sm text-orange-600' : 'text-sm text-gray-600'} mb-1`}>Active</p>
                    <p className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-green-600' : 'text-2xl font-bold text-green-600'}`}>
                      {referralAnalytics.activeCodes || 0}
                    </p>
                  </div>
                  <div className={`${isPWA ? 'bg-white/80 border-2 border-orange-200/50' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-3 sm:p-4 rounded-xl' : 'p-4 rounded-lg'}`}>
                    <p className={`${isPWA ? 'text-xs sm:text-sm text-orange-600' : 'text-sm text-gray-600'} mb-1`}>Pending</p>
                    <p className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-yellow-600' : 'text-2xl font-bold text-yellow-600'}`}>
                      {referralAnalytics.pendingCodes || 0}
                    </p>
                  </div>
                  <div className={`${isPWA ? 'bg-white/80 border-2 border-orange-200/50' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-3 sm:p-4 rounded-xl' : 'p-4 rounded-lg'}`}>
                    <p className={`${isPWA ? 'text-xs sm:text-sm text-orange-600' : 'text-sm text-gray-600'} mb-1`}>Used</p>
                    <p className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-blue-600' : 'text-2xl font-bold text-blue-600'}`}>
                      {referralAnalytics.usedCodes || 0}
                    </p>
                  </div>
                  <div className={`${isPWA ? 'bg-white/80 border-2 border-orange-200/50' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-3 sm:p-4 rounded-xl' : 'p-4 rounded-lg'}`}>
                    <p className={`${isPWA ? 'text-xs sm:text-sm text-orange-600' : 'text-sm text-gray-600'} mb-1`}>Total Referrals</p>
                    <p className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-purple-600' : 'text-2xl font-bold text-purple-600'}`}>
                      {referralAnalytics.totalReferrals || 0}
                    </p>
                  </div>
                </div>

                {/* Statistics by Discount Percentage */}
                {referralAnalytics.byDiscount && referralAnalytics.byDiscount.length > 0 && (
                  <div className="mt-6">
                    <h4 className={`${isPWA ? 'text-base sm:text-lg font-bold text-orange-800 mb-3' : 'text-base font-medium text-gray-900 mb-3'}`}>
                      Statistics by Discount
                    </h4>
                    <div className={`${isPWA ? 'space-y-3' : 'space-y-4'}`}>
                      {referralAnalytics.byDiscount.map((discountStats, index) => (
                        <div 
                          key={index}
                          className={`${isPWA ? 'bg-white/80 border-2 border-orange-200/50' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-3 sm:p-4 rounded-xl' : 'p-4 rounded-lg'}`}
                        >
                          <div className={`flex items-center justify-between mb-3 ${isPWA ? 'pb-2 border-b border-orange-200/50' : 'pb-2 border-b border-gray-200'}`}>
                            <h5 className={`${isPWA ? 'text-sm sm:text-base font-bold text-orange-800' : 'text-base font-semibold text-gray-900'}`}>
                              {discountStats.discountType === 'percentage' 
                                ? `${discountStats.discountValue}% Discount`
                                : `₹${discountStats.discountValue} Discount`}
                            </h5>
                            <span className={`${isPWA ? 'text-xs sm:text-sm text-orange-600' : 'text-sm text-gray-600'}`}>
                              {discountStats.totalCodes} {discountStats.totalCodes === 1 ? 'code' : 'codes'}
                            </span>
                          </div>
                          <div className={`grid ${isPWA ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 md:grid-cols-4'} ${isPWA ? 'gap-2 sm:gap-3' : 'gap-3'}`}>
                            <div>
                              <p className={`${isPWA ? 'text-xs text-orange-600' : 'text-xs text-gray-600'} mb-1`}>Active</p>
                              <p className={`${isPWA ? 'text-lg font-bold text-green-600' : 'text-lg font-bold text-green-600'}`}>
                                {discountStats.activeCodes || 0}
                              </p>
                            </div>
                            <div>
                              <p className={`${isPWA ? 'text-xs text-orange-600' : 'text-xs text-gray-600'} mb-1`}>Pending</p>
                              <p className={`${isPWA ? 'text-lg font-bold text-yellow-600' : 'text-lg font-bold text-yellow-600'}`}>
                                {discountStats.pendingCodes || 0}
                              </p>
                            </div>
                            <div>
                              <p className={`${isPWA ? 'text-xs text-orange-600' : 'text-xs text-gray-600'} mb-1`}>Used</p>
                              <p className={`${isPWA ? 'text-lg font-bold text-blue-600' : 'text-lg font-bold text-blue-600'}`}>
                                {discountStats.usedCodes || 0}
                              </p>
                            </div>
                            <div>
                              <p className={`${isPWA ? 'text-xs text-orange-600' : 'text-xs text-gray-600'} mb-1`}>Referrals</p>
                              <p className={`${isPWA ? 'text-lg font-bold text-purple-600' : 'text-lg font-bold text-purple-600'}`}>
                                {discountStats.totalReferrals || 0}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  if (isLoading && isInitialFetch.current) {
  return (
    <div className="p-6 max-w-6xl mx-auto bg-white rounded-lg shadow-sm text-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading data...</p>
    </div>
  );
  }

  if (!isLoading && isInitialFetch.current === false && memoizedPrograms.length === 0) {
    return (
      <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-white' : 'bg-gray-50'} py-12 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm text-center py-12 px-6">
            <h2 className="text-xl font-medium text-gray-700 mb-4">No programs available</h2>
            <p className="text-gray-600 mb-6">You need to create Programs first, before managing pricing and discounts.</p>
            <button
              onClick={() => navigate('/trainer/programs')}
              className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 shadow-sm transition-colors"
            >
              <FaPlus className="inline mr-1" />
              Add New Program
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation Modal
  const ConfirmationModal = () => {
    if (!showDeleteModal) return null;

    const getItemName = () => {
      if (!deleteItem) return '';
      switch (deleteType) {
        case 'promo':
          return deleteItem.code;
        case 'discount':
          return deleteItem.name;
        case 'installment':
          return deleteItem.name;
        case 'scholarship':
          return deleteItem.name;
        default:
          return 'this item';
      }
    };

    const getItemType = () => {
      switch (deleteType) {
        case 'promo':
          return 'promo code';
        case 'discount':
          return 'discount';
        case 'installment':
          return 'installment plan';
        case 'scholarship':
          return 'scholarship offer';
        default:
          return 'item';
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <FaTrash className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Confirm Deletion</h3>
            </div>
          </div>
          <div className="mb-6">
            {deleteType === 'program' && deleteItem?.dependencies && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm font-medium text-yellow-800 mb-2">⚠️ Dependencies Found</p>
                <p className="text-sm text-yellow-700 mb-2">
                  {deleteItem.errorMessage || `This program has dependencies that must be removed first.`}
                </p>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {deleteItem.dependencies.classes > 0 && (
                    <li>• {deleteItem.dependencies.classes} active class{deleteItem.dependencies.classes !== 1 ? 'es' : ''}</li>
                  )}
                  {deleteItem.dependencies.students > 0 && (
                    <li>• {deleteItem.dependencies.students} enrolled student{deleteItem.dependencies.students !== 1 ? 's' : ''}</li>
                  )}
                </ul>
              </div>
            )}
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <span className="font-medium">{getItemName()}</span>? 
              This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                handleDeleteCancel();
                setShowForceDeleteConfirm(false);
              }}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            {deleteType === 'program' && deleteItem?.dependencies ? (
              <>
                <button
                  onClick={() => setShowForceDeleteConfirm(true)}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Force Delete
                </button>
              </>
            ) : (
              <button
                onClick={handleDeleteConfirm}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Tab configuration - filter out unnecessary tabs for consultants
  const allTabs = [
    { id: 0, label: 'Program Pricing', icon: <FaRupeeSign /> },
    { id: 1, label: 'Promo Codes', icon: <FaTags /> },
    { id: 2, label: 'Discounts', icon: <FaPercent /> },
    { id: 3, label: 'Installment Plans', icon: <FaCreditCard /> },
    { id: 4, label: 'Scholarships', icon: <FaGraduationCap /> },
    { id: 5, label: 'Referral Settings', icon: <FaUserFriends /> },
  ];
  
  // For consultants, remove Program Pricing (0), Installment Plans (3), and Scholarships (4)
  const tabs = isConsultant 
    ? allTabs.filter(tab => ![0, 3, 4].includes(tab.id))
    : allTabs;

  return (
    <ErrorBoundary>
      <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-white' : 'bg-gray-50'} py-12 px-4 sm:px-6 lg:px-8`}>
        {/* Header Section */}
        <div className={`relative ${isPWA ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50' : 'bg-gradient-to-r from-orange-600 to-amber-700'} ${isPWA ? 'text-gray-900' : 'text-white'} rounded-2xl ${isPWA ? 'mb-6 sm:mb-8' : 'mb-8'} overflow-hidden`}>
          {isPWA && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
          )}
          {!isPWA && (
            <div className="absolute inset-0 bg-black opacity-20"></div>
          )}
          <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-orange-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'} flex items-center gap-2`}>
                  <FaMoneyBillWave className={isPWA ? 'text-orange-600' : 'text-white'} />
                  Fee Plan
                </h1>
                <p className={`${isPWA ? 'text-sm sm:text-base text-orange-700' : 'text-white/90'}`}>
                  Manage your programs, promotions, and payment plans
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto">
        {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <FaTimes className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error occurred</h3>
                  <p className="mt-1 text-sm text-red-700">{error.message || 'Something went wrong. Please try again.'}</p>
              </div>
            </div>
          </div>
        )}

          {/* Stats Cards Section */}
          <div className={`mb-6 ${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl rounded-2xl p-4 sm:p-6' : 'bg-white rounded-lg shadow-sm border border-gray-200 p-6'}`}>
            <div className={`grid ${isPWA ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'} gap-4 sm:gap-6`}>
              <div className={`${isPWA ? 'bg-gradient-to-br from-white via-blue-50/40 to-blue-100/30 border-2 border-blue-200/60 shadow-lg' : 'bg-white rounded-lg shadow border border-gray-200'} ${isPWA ? 'p-4 sm:p-5' : 'p-6'} rounded-lg`}>
                <div className="flex items-center">
                  <div className={`p-3 rounded-full ${isPWA ? 'bg-blue-100/80' : 'bg-orange-100'} ${isPWA ? 'text-blue-600' : 'text-orange-600'} mr-4`}>
                    <FaChartBar className="text-xl" />
                  </div>
                  <div>
                    <h3 className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-blue-700' : 'text-gray-500'} font-medium`}>Active Programs</h3>
                    <p className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-blue-600' : 'text-orange-600'}`}>{dashboardStats.activePrograms} / {dashboardStats.totalPrograms}</p>
                  </div>
                </div>
              </div>

              <div className={`${isPWA ? 'bg-gradient-to-br from-white via-purple-50/40 to-purple-100/30 border-2 border-purple-200/60 shadow-lg' : 'bg-white rounded-lg shadow border border-gray-200'} ${isPWA ? 'p-4 sm:p-5' : 'p-6'} rounded-lg`}>
                <div className="flex items-center">
                  <div className={`p-3 rounded-full ${isPWA ? 'bg-purple-100/80' : 'bg-amber-100'} ${isPWA ? 'text-purple-600' : 'text-amber-600'} mr-4`}>
                    <FaCalendarAlt className="text-xl" />
                  </div>
                  <div>
                    <h3 className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-purple-700' : 'text-gray-500'} font-medium`}>Active Promos</h3>
                    <p className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-purple-600' : 'text-amber-600'}`}>{dashboardStats.activePromos}</p>
                  </div>
                </div>
              </div>

              <div className={`${isPWA ? 'bg-gradient-to-br from-white via-indigo-50/40 to-indigo-100/30 border-2 border-indigo-200/60 shadow-lg' : 'bg-white rounded-lg shadow border border-gray-200'} ${isPWA ? 'p-4 sm:p-5' : 'p-6'} rounded-lg`}>
                <div className="flex items-center">
                  <div className={`p-3 rounded-full ${isPWA ? 'bg-indigo-100/80' : 'bg-yellow-100'} ${isPWA ? 'text-indigo-600' : 'text-yellow-600'} mr-4`}>
                    <FaCalendarAlt className="text-xl" />
                  </div>
                  <div>
                    <h3 className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-indigo-700' : 'text-gray-500'} font-medium`}>Active Discounts</h3>
                    <p className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-indigo-600' : 'text-yellow-600'}`}>{dashboardStats.activeDiscounts}</p>
                  </div>
                </div>
              </div>

              {!isConsultant && (
                <div className={`${isPWA ? 'bg-gradient-to-br from-white via-teal-50/40 to-orange-50/30 border-2 border-orange-200/60 shadow-lg' : 'bg-white rounded-lg shadow border border-gray-200'} ${isPWA ? 'p-4 sm:p-5' : 'p-6'} rounded-lg`}>
                  <div className="flex items-center">
                    <div className={`p-3 rounded-full ${isPWA ? 'bg-teal-100/80' : 'bg-orange-100'} ${isPWA ? 'text-orange-600' : 'text-orange-600'} mr-4`}>
                      <FaUserGraduate className="text-xl" />
                    </div>
                    <div>
                      <h3 className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700' : 'text-gray-500'} font-medium`}>Pending Scholarships</h3>
                      <p className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-600' : 'text-orange-600'}`}>{dashboardStats.pendingApplications}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tab Navigation - Only show in browser mode */}
          {!isPWA && (
            <Tabs selectedIndex={activeTab} onSelect={index => setActiveTab(index)}>
              <TabList className="flex border-b border-gray-200 mb-6">
                {tabs.map((tab) => (
                  <Tab 
                    key={tab.id}
                    className="px-4 py-2 font-medium text-sm focus:outline-none cursor-pointer"
                    selectedClassName="border-b-2 border-orange-500 text-orange-600"
                  >
                    <div className="flex items-center gap-2">
                      {tab.icon}
                      {tab.label}
                    </div>
                  </Tab>
                ))}
              </TabList>

              {!isConsultant && (
                <TabPanel>
                  {PricingTabPanel}
                </TabPanel>
              )}
              <TabPanel>
                {PromoCodesTabPanel}
              </TabPanel>
              <TabPanel>
                {DiscountsTabPanel}
              </TabPanel>
              {!isConsultant && (
                <>
                  <TabPanel>
                    {InstallmentTabPanel}
                  </TabPanel>
                  <TabPanel>
                    {ScholarshipTabPanel}
                  </TabPanel>
                </>
              )}
              <TabPanel>
                {ReferralSettingsTabPanel}
              </TabPanel>
            </Tabs>
          )}

          {/* Card Navigation - Only show in PWA mode */}
          {isPWA && (
            <>
              {/* Navigation Cards Section - Similar to HomeTrainer */}
              <div className="mb-6">
                <div className={`${isPWA ? 'bg-gradient-to-br from-white via-orange-50/30 to-white border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} ${isPWA ? 'rounded-2xl' : ''} ${isPWA ? 'p-4 sm:p-6' : 'p-6'} relative overflow-hidden`}>
                  {isPWA && (
                    <>
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/60 to-transparent"></div>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-2xl"></div>
                    </>
                  )}
                  <div className={`flex justify-between items-center ${isPWA ? 'mb-5 sm:mb-6 px-2' : 'mb-6'} relative z-10`}>
                    <div className="flex items-center gap-3 relative">
                      {isPWA && (
                        <>
                          <div className="absolute left-0 w-1.5 h-8 bg-gradient-to-b from-orange-400/30 via-amber-400/30 to-yellow-400/30 rounded-full blur-sm"></div>
                          <div className="relative w-1.5 h-8 bg-gradient-to-b from-orange-500 via-amber-500 to-yellow-500 rounded-full shadow-md"></div>
                        </>
                      )}
                      <div>
                        <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-800'} ${isPWA ? 'drop-shadow-sm' : ''}`}>Sections</h2>
                        {isPWA && (
                          <p className="text-xs text-orange-600/70 mt-0.5 font-medium">Manage pricing & offers</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className={`flex ${isPWA ? 'flex-nowrap gap-3 sm:gap-4 overflow-x-auto overflow-y-visible pb-3 scrollbar-hide -mx-2 px-2 sm:mx-0 sm:px-0 pt-2' : 'flex-wrap gap-4'} relative z-10`}>
                    {tabs.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveTab(item.id)}
                        className={`${isPWA ? 'bg-gradient-to-br from-orange-50 via-amber-50/80 to-white' : 'bg-white'} ${isPWA ? 'text-orange-800' : 'text-gray-700'} ${isPWA ? 'p-4 sm:p-5 min-w-[110px] sm:min-w-[130px] flex-shrink-0' : 'p-4'} rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl ${isPWA ? 'hover:-translate-y-2' : 'hover:-translate-y-1'} border-2 ${activeTab === item.id ? isPWA ? 'border-orange-400/70 ring-2 ring-orange-200/50' : 'border-orange-500 ring-2 ring-orange-200' : isPWA ? 'border-orange-200/60' : 'border-gray-200'} hover:border-orange-400/70 active:scale-95 flex flex-col items-center text-center group relative overflow-visible`}
                      >
                        {/* Top accent line on card */}
                        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        
                        {/* Decorative gradient overlay */}
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-200/30 to-amber-200/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"></div>
                        
                        {/* Icon container with enhanced styling */}
                        <div className={`${isPWA ? 'bg-gradient-to-br from-orange-100 to-amber-100' : 'bg-gray-100'} ${isPWA ? 'p-3 sm:p-4' : 'p-3'} rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg ring-2 ring-orange-100/60 group-hover:ring-orange-200/80 relative z-10`}>
                          <div className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-2xl'}`}>
                            {item.icon}
                          </div>
                        </div>
                        
                        {/* Title with enhanced styling */}
                        <h3 className={`${isPWA ? 'text-[11px] sm:text-xs' : 'text-xs'} font-bold relative z-10 leading-tight text-center`}>{item.label}</h3>
                        
                        {/* Bottom accent on hover */}
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-400/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content Panels */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {!isConsultant && activeTab === 0 && PricingTabPanel}
                {activeTab === (isConsultant ? 0 : 1) && PromoCodesTabPanel}
                {activeTab === (isConsultant ? 1 : 2) && DiscountsTabPanel}
                {!isConsultant && activeTab === 3 && InstallmentTabPanel}
                {!isConsultant && activeTab === 4 && ScholarshipTabPanel}
                {activeTab === (isConsultant ? 2 : 5) && ReferralSettingsTabPanel}
              </div>
            </>
          )}
        </div>
        
        <ConfirmationModal />
        
        {/* Force Delete Confirmation for Programs */}
        {showForceDeleteConfirm && deleteType === 'program' && deleteItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex items-start mb-4">
                <FaTrash className="h-6 w-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Force Delete Program</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This will permanently delete the program <span className="font-medium">{deleteItem.name}</span> and may affect {deleteItem.dependencies?.classes || 0} classes and {deleteItem.dependencies?.students || 0} students. This action cannot be undone.
                  </p>
                  <p className="text-sm font-medium text-red-600">
                    Are you absolutely sure you want to proceed?
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowForceDeleteConfirm(false)}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowForceDeleteConfirm(false);
                    handleDeleteConfirm();
                  }}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  {isLoading ? 'Deleting...' : 'Yes, Force Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Scholarship Application Delete Confirmation */}
        <ConfirmDialog
          isOpen={showDeleteScholarshipConfirm !== null}
          onClose={() => setShowDeleteScholarshipConfirm(null)}
          onConfirm={() => {
            if (showDeleteScholarshipConfirm) {
              updateScholarshipApplicationStatus(showDeleteScholarshipConfirm, 'deleted');
            }
          }}
          title="Delete Scholarship Application"
          message="Are you sure you want to delete this application? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />

        {/* Referral System Toggle Confirmation */}
        <ConfirmDialog
          isOpen={showReferralSystemConfirm}
          onClose={() => {
            setShowReferralSystemConfirm(false);
            setPendingReferralSystemEnabled(null);
          }}
          onConfirm={handleReferralSystemToggle}
          title={pendingReferralSystemEnabled ? "Enable Referral System" : "Disable Referral System"}
          message={
            pendingReferralSystemEnabled
              ? "Are you sure you want to enable the referral system? New referral codes will be generated for students after their first payment."
              : "Are you sure you want to disable the referral system? No new referral codes will be generated. However, all existing codes will remain valid and must be honored. You are liable to honor all distributed codes even if the system is disabled."
          }
          confirmText={pendingReferralSystemEnabled ? "Enable" : "Disable"}
          cancelText="Cancel"
          variant={pendingReferralSystemEnabled ? "default" : "danger"}
        />
      </div>
    </ErrorBoundary>
  );
}

export default FeePlan;